/**
 * AG Grid accessibility utilities.
 */

/**
 * Locale-text overrides that suppress AG Grid's generic row-selection
 * announcements (WCAG 4.1.2 / 1.3.1 — avoid misleading screen-reader output).
 *
 * AG Grid announces "Press SPACE to select this row." / "...deselect this
 * row." into a single shared `aria-live` region (`.ag-aria-description-
 * container`) from `RowCtrl.announceDescription()`. Because that region is the
 * same one used for header sort hints, the row-selection text leaks into the
 * header context — e.g. after activating a sortable column header the infinite
 * row model purges and re-applies selection, re-firing the announcement so JAWS
 * reads "Press SPACE to select this row" while the user is on a column header.
 *
 * These landing-page grids already give each row checkbox a meaningful
 * `aria-label` (the row/plan name) and set `aria-selected` on the row, so the
 * generic hint is redundant. Blanking the keys removes the spurious text
 * without losing real selection state, which `aria-selected` still conveys.
 *
 * Bind via `[localeText]="GRID_ARIA_LOCALE_TEXT"` on `<ag-grid-angular>`.
 */
export const GRID_ARIA_LOCALE_TEXT: Record<string, string> = {
  ariaRowSelect: "",
  ariaRowDeselect: "",
  ariaRowSelectAll: "",
  ariaRowToggleSelection: "",
};


/**
 * Fix the ARIA role on flat AG Grids (WCAG 4.1.2 Name, Role, Value).
 *
 * AG Grid v31 sets `role="treegrid"` on its root element by default. A
 * tree grid implies hierarchical/expandable rows (ARIA APG: treegrid). For
 * flat data tables that have no expand/collapse controls, screen readers
 * announce them incorrectly as "tree grid". Pass the component's host
 * element (via ElementRef.nativeElement) from `onGridReady` to downgrade
 * the role to the correct `role="grid"`.
 *
 * Do NOT use this on grids that actually have `treeData: true`,
 * master/detail rows, or row grouping with collapse/expand.
 */
export function setFlatGridRole(hostEl: HTMLElement | null | undefined): void {
  if (!hostEl) return;
  const treeGridEl = hostEl.querySelector('[role="treegrid"]');
  if (treeGridEl) {
    treeGridEl.setAttribute("role", "grid");
  }
}

/**
 * Toggle a grid's navigation role between `grid` and `treegrid` for a grid
 * whose rows are flat in one view and hierarchical in another
 * (WCAG 4.1.2 Name, Role, Value / 1.3.1 Info and Relationships; ARIA APG Grid
 * vs Treegrid).
 *
 * AG Grid v31 hard-codes `role="treegrid"` on its root element. JAWS treats a
 * treegrid as a TREE control, so its table-reading / table quick-navigation
 * keys (e.g. Ctrl+Alt+Arrow) do not engage — from a header it will not move
 * into the body and it announces "end of row" instead of stepping across
 * columns. The CT (flat) Plan view has NO expand/collapse rows, so `role="grid"`
 * is the correct semantic and lets JAWS navigate rows/columns and announce the
 * associated row/column headers. The MU view has real expandable group rows, so
 * it must keep `role="treegrid"`.
 *
 * Pass the component host element (ElementRef.nativeElement) and whether the
 * grid is currently flat. AG Grid never re-asserts the role after creation and
 * keeps the same root element across data/column changes, so this is safe to
 * call repeatedly (e.g. after a CT<->MU view switch) — the last call wins.
 */
export function setGridNavigationRole(
  hostEl: HTMLElement | null | undefined,
  isFlat: boolean
): void {
  if (!hostEl) return;
  const gridEl = hostEl.querySelector('[role="treegrid"], [role="grid"]');
  if (!gridEl) return;
  gridEl.setAttribute("role", isFlat ? "grid" : "treegrid");
}

/**
 * Resolve AG Grid's "non-active element in tab order" findings
 * (WCAG 4.1.2 Name, Role, Value).
 *
 * AG Grid renders two kinds of focusable-but-presentational chrome that an
 * accessibility scanner (e.g. arc) flags because they sit in the tab order via
 * tabindex while declaring role="presentation":
 *
 * 1. The scrollable body viewport (.ag-body-viewport). It is a structural
 *    scroll wrapper around the row group, so it must NOT be a tab stop at all —
 *    the grid stays keyboard operable through its cells (arrow keys, Home/End)
 *    and the tab guards. We therefore strip any stray tabindex from it.
 *
 * 2. The top/bottom tab guards (.ag-tab-guard). These MUST keep tabindex="0"
 *    so keyboard users can Tab into and out of the grid (removing it would make
 *    the grid unreachable — a worse WCAG 2.1.1 failure). A focusable element
 *    with role="presentation" is contradictory, so we promote them to
 *    role="group": a valid, specific ARIA role that satisfies the scanner,
 *    needs no required children/parent (so it does not disturb the grid's
 *    row/rowgroup structure), and does not interfere with AG Grid's
 *    tabindex-and-event-driven focus trap.
 *
 * Pass the component host element from `onGridReady` (ElementRef.nativeElement).
 */
export function fixGridNonActiveTabOrder(hostEl: HTMLElement | null | undefined): void {
  if (!hostEl) return;

  const viewport = hostEl.querySelector<HTMLElement>(".ag-body-viewport");
  // A role="presentation" scroll wrapper must not sit in the tab order.
  if (viewport && (viewport.getAttribute("role") === "presentation" || !viewport.getAttribute("role"))) {
    viewport.removeAttribute("tabindex");
  }

  // Focusable tab guards must expose a specific role instead of "presentation".
  hostEl.querySelectorAll<HTMLElement>(".ag-tab-guard").forEach((guard) => {
    const role = guard.getAttribute("role");
    if (guard.getAttribute("tabindex") === "0" && (role === "presentation" || !role)) {
      guard.setAttribute("role", "group");
    }
  });
}

/**
 * Make AG Grid's scrollable body viewport keyboard accessible
 * (WCAG 2.1.1 Keyboard / axe "scrollable-region-focusable").
 *
 * The grid body (.ag-body-viewport) scrolls when there are more rows than fit,
 * but its cells use tabindex="-1" (AG Grid manages focus), so nothing inside it
 * sits in the tab sequence. Browsers such as Safari therefore never let keyboard
 * users reach or scroll the region. Adding a single tabindex="0" tab stop makes
 * the scroll region reachable so arrow / Page keys can scroll it.
 *
 * The tab stop is placed on the ROWGROUP CONTAINER (.ag-center-cols-container,
 * role="rowgroup"), NOT on the wrapping viewports. The viewports are
 * role="presentation", and a focusable presentation element is flagged
 * "non-active element in tab order"; giving them a real role instead orphans the
 * rowgroup. The rowgroup already has a specific role, is a valid grid child, and
 * a focusable descendant satisfies the scroll-region check for both viewports.
 *
 * Per a11y guidance, the tab stop is only added when the viewport has NO other
 * tab-focusable descendant, so a grid that already exposes focusable content is
 * not given a redundant extra tab stop. Pass the component host element from
 * `onGridReady` (ElementRef.nativeElement).
 *
 * NOTE (ESP-21579): this helper deliberately installs NO header <-> body
 * horizontal scroll sync. AG Grid keeps the header and body aligned during
 * keyboard navigation itself (native header nav -> `ensureColumnVisible`, and
 * body-cell nav scrolls the same source-of-truth viewport). An earlier
 * hand-rolled focusin/scroll mirror fought that native scroll and CAUSED the
 * "header moves but body doesn't" / "first column misaligned after Shift+Tab"
 * desync, so it was removed; grids must instead let AG Grid own the header Tab
 * navigation (do not set `suppressHeaderKeyboardEvent` for Tab). Installs no
 * listeners and returns null.
 */
export function makeGridScrollRegionFocusable(
  hostEl: HTMLElement | null | undefined
): (() => void) | null {
  if (!hostEl) return null;
  // The element that actually overflows is the center columns viewport; on
  // grids without horizontal scroll AG Grid sizes it the same as the body
  // viewport, so prefer it and fall back to the body viewport.
  const viewport =
    hostEl.querySelector<HTMLElement>(".ag-center-cols-viewport") ||
    hostEl.querySelector<HTMLElement>(".ag-body-viewport");
  if (!viewport) return null;

  // ---------------------------------------------------------------------------
  // Header <-> body horizontal scroll sync (ESP-21579): intentionally NONE here.
  //
  // AG Grid already keeps the header and body horizontally aligned during
  // keyboard navigation on its own. GridHeaderCtrl.onTabKeyDown ->
  // navigateHorizontally -> scrollToColumn -> ensureColumnVisible moves the
  // header transform AND the body columns together from AG Grid's single source
  // of truth (`.ag-body-horizontal-scroll-viewport`), and body-cell navigation
  // scrolls that same viewport. A hand-rolled focusin/scroll mirror layered on
  // top of that FOUGHT AG Grid's native scroll (double-scroll + requestAnimation
  // Frame races) and was the actual cause of the "header moves but body doesn't"
  // and "first column misaligned after Shift+Tab" desync. The correct fix is to
  // let AG Grid own the scroll — plan-detail-v5 no longer sets
  // `suppressHeaderKeyboardEvent` for Tab, so AG Grid's native header navigation
  // (not the browser) drives Tab/Shift+Tab across the header — and to add NO
  // custom horizontal sync here.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Make the scroll region keyboard-focusable (WCAG 1.3.1 / 2.4.7) — ONLY when
  // the body has no genuine tab stop of its own.
  //
  // A genuinely tab-focusable descendant must NOT be removed from the tab order
  // with tabindex="-1". AG Grid renders cells and selection checkboxes as
  // natively-focusable elements (e.g. <input type="checkbox">) but sets
  // tabindex="-1" on them and drives focus itself — so they are not real tab
  // stops and must be ignored here.
  const candidates = viewport.querySelectorAll<HTMLElement>(
    "a[href], button, input, select, textarea, [tabindex]"
  );
  const hasTabbable = Array.from(candidates).some((el) => {
    const isDisabled =
      "disabled" in el &&
      (el as HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled;
    const isAriaDisabled = el.getAttribute("aria-disabled") === "true";
    // tabIndex >= 0 covers natively-focusable elements and explicit tabindex,
    // while excluding any negative value (e.g. "-1", "-2") that removes the
    // element from the tab order.
    return !isDisabled && !isAriaDisabled && el.tabIndex >= 0;
  });
  if (!hasTabbable) {
    // The scroll wrappers (.ag-body-viewport / .ag-center-cols-viewport) sit
    // between the grid and its rowgroup, so they can NOT take the tab stop: any
    // tabindex on a role="presentation" element is flagged "non-active element in
    // tab order" (it lacks a specific role), and giving them a real role orphans
    // the rowgroup. Instead make the ROWGROUP CONTAINER itself focusable: it
    // already has the specific role="rowgroup", is a valid grid child, and a
    // focusable descendant satisfies the "scrollable region focusable" check for
    // both viewports — so nothing is flagged.
    const rowGroup =
      viewport.querySelector<HTMLElement>('[role="rowgroup"]') ||
      hostEl.querySelector<HTMLElement>('[role="rowgroup"]');
    if (rowGroup && !rowGroup.hasAttribute("tabindex")) {
      rowGroup.setAttribute("tabindex", "0");
    }
  }

  // No listeners are installed (the WCAG tab-stop above only sets an attribute,
  // and header/body scroll sync is left to AG Grid's native navigation), so
  // there is nothing to clean up.
  return null;
}

/**
 * Clear the grid's focused cell and range/cell selection when keyboard or
 * pointer focus leaves the grid entirely (WCAG 2.4.7 Focus Visible).
 *
 * AG Grid keeps its focused-cell highlight AND its range selection (with
 * `enableRangeSelection`) even after focus moves OUTSIDE the grid — clicking a
 * button elsewhere, or Tabbing out. Because those are painted from persistent
 * state (the `.ag-cell-focus` class and the range-selection cell borders),
 * NOT from `:focus`, the purple cell outline lingered as a "stuck" focus
 * indicator with nothing actually focused.
 *
 * This listens for `focusout` on the grid host and, once focus has settled
 * OUTSIDE the grid root, clears the focused cell and the cell selection so no
 * focus/selection indicator remains. Focus MOVING WITHIN the grid (cell to
 * cell, into an editor) is ignored. A pointer click that lands on a
 * non-focusable area outside the grid does not always fire `focusout`, so a
 * capture-phase document `pointerdown` outside the grid clears it too.
 *
 * Returns a cleanup function; call it from the component's `ngOnDestroy`.
 */
export function clearGridSelectionOnBlur(
  hostEl: HTMLElement | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi?: any
): () => void {
  if (!hostEl) return () => {};

  const clear = (): void => {
    if (!gridApi) return;
    if (typeof gridApi.clearFocusedCell === "function") {
      gridApi.clearFocusedCell();
    }
    // v31 renamed clearRangeSelection → clearCellSelection; support both.
    if (typeof gridApi.clearCellSelection === "function") {
      gridApi.clearCellSelection();
    } else if (typeof gridApi.clearRangeSelection === "function") {
      gridApi.clearRangeSelection();
    }
  };

  const gridRoot =
    hostEl.querySelector<HTMLElement>(".ag-root-wrapper") || hostEl;

  const onFocusOut = (event: FocusEvent): void => {
    const next = event.relatedTarget;
    // Focus stayed inside the grid (cell → cell, into an editor) — leave it.
    if (next instanceof Node && gridRoot.contains(next)) return;
    // relatedTarget can be null (e.g. focus dropped to <body>); confirm on the
    // next tick that focus really is outside the grid before clearing.
    setTimeout(() => {
      const active = hostEl.ownerDocument.activeElement;
      if (!active || !gridRoot.contains(active)) {
        clear();
      }
    });
  };

  const onDocPointerDown = (event: Event): void => {
    const target = event.target;
    if (target instanceof Node && !gridRoot.contains(target)) {
      clear();
    }
  };

  hostEl.addEventListener("focusout", onFocusOut);
  const doc = hostEl.ownerDocument;
  doc.addEventListener("pointerdown", onDocPointerDown, true);

  return () => {
    hostEl.removeEventListener("focusout", onFocusOut);
    doc.removeEventListener("pointerdown", onDocPointerDown, true);
  };
}


/**
 * ARIA grid keyboard pattern for the row-selection checkbox column
 * (WCAG 2.4.3 Focus Order, 2.4.7 Focus Visible).
 *
 * AG Grid renders the row-selection checkbox as a native <input> and, while
 * navigating with Tab, drives focus onto that <input> directly. That makes the
 * focus ring land on the small checkbox instead of the row-select CELL, and —
 * when the checkbox is left in the Tab order — produces a redundant second stop
 * (cell + checkbox) on the same column. The rest of this app follows the
 * pattern where the CELL is the single Tab stop and its interactive child is
 * reached via the cell (Space/Enter toggles selection when the cell is
 * focused). This helper enforces that pattern by bouncing focus off the
 * selection checkbox back onto its owning grid cell whenever AG Grid focuses
 * the checkbox. Space/Enter on the focused cell still toggle selection, so the
 * checkbox stays fully keyboard-operable.
 *
 * Returns a cleanup function that removes the listener (call it from
 * ngOnDestroy), or null when there is no host element.
 */
export function redirectSelectionCheckboxFocusToCell(
  hostEl: HTMLElement | null | undefined
): (() => void) | null {
  if (!hostEl) return null;
  const onFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target || !target.matches?.(".ag-selection-checkbox input")) return;
    const cell = target.closest<HTMLElement>(".ag-cell");
    // Only redirect when focus is genuinely on the checkbox itself; if AG Grid
    // is mid-edit or the cell is missing, leave focus alone.
    if (cell) cell.focus();
  };
  hostEl.addEventListener("focusin", onFocusIn, true);
  return () => hostEl.removeEventListener("focusin", onFocusIn, true);
}

/**
 * AG Grid rowgroup containers (other than the center/pinned BODY containers
 * that hold the actual data rows) that AG Grid renders as part of its skeleton
 * with `role="rowgroup"` and that are EMPTY on a flat grid: the full-width row
 * host plus the sticky top/bottom pinned, center and full-width containers.
 * Each is an empty `role="rowgroup"` that ARC/axe flag as "the rowgroup role is
 * missing one of the row elements" (WCAG 4.1.2). The center/pinned BODY
 * containers are deliberately excluded so their rowgroup role — and the
 * grid scroll-region tab stop placed on it — survive.
 */
const EMPTY_PRONE_ROWGROUP_SELECTOR = [
  ".ag-full-width-container",
  ".ag-sticky-top-container",
  ".ag-sticky-top-full-width-container",
  ".ag-pinned-left-sticky-top",
  ".ag-pinned-right-sticky-top",
  ".ag-sticky-bottom-container",
  ".ag-sticky-bottom-full-width-container",
  ".ag-pinned-left-sticky-bottom",
  ".ag-pinned-right-sticky-bottom",
].join(", ");

/**
 * Fix AG Grid's empty rowgroup containers
 * (WCAG 4.1.2 Name, Role, Value / 1.3.1 Info and Relationships).
 *
 * AG Grid renders several containers with `role="rowgroup"` as part of its
 * skeleton: `.ag-full-width-container` (host for full-width rows — group rows,
 * master/detail and full-width cell renderers) and the sticky top/bottom
 * pinned, center and full-width containers (`.ag-pinned-left-sticky-top`,
 * `.ag-sticky-top-container`, etc. — hosts for pinned/sticky rows). On a flat
 * grid, or whenever there are no such rows on screen, these containers have NO
 * `role="row"` children, so a scanner (e.g. arc) flags "the rowgroup role is
 * missing one of the row elements". An empty rowgroup conveys no table
 * structure, so we downgrade the EMPTY container to `role="presentation"`; as
 * soon as AG Grid renders rows into it we restore `role="rowgroup"` so the real
 * structure is exposed again.
 *
 * AG Grid adds/removes these rows on grouping, master/detail expansion, sticky
 * grouping and virtual scrolling and may re-assert `role="rowgroup"`, so this
 * is wired to a MutationObserver rather than run once. Each container is
 * observed for its own child/role changes (normal cells live in a different
 * container and do not trigger it), and each container's parent viewport is
 * watched with a childList observer so a container that AG Grid destroys and
 * RECREATES (e.g. a view-mode switch or column change, which do not re-fire
 * `onGridReady`) is re-fixed and re-observed. Returns a disconnectable so the
 * caller can tear down every observer on destroy (pass `null`-safe).
 *
 * Pass the component host element from `onGridReady` (ElementRef.nativeElement).
 */
export function fixEmptyFullWidthRowGroup(
  hostEl: HTMLElement | null | undefined
): { disconnect(): void } | null {
  if (!hostEl) return null;

  const apply = (container: HTMLElement) => {
    const hasRow = !!container.querySelector(':scope > [role="row"]');
    if (hasRow) {
      if (container.getAttribute("role") !== "rowgroup") {
        container.setAttribute("role", "rowgroup");
      }
    } else if (container.getAttribute("role") === "rowgroup") {
      container.setAttribute("role", "presentation");
    }
  };

  // Re-fix the role whenever a container gains/loses full-width rows or AG Grid
  // re-asserts role="rowgroup" on the same node. Each container gets its OWN
  // observer (tracked in a Map) so we can stop observing a container that AG
  // Grid removes from the DOM — a single shared observer could only be torn
  // down all-at-once, leaving detached nodes observed for the grid's lifetime.
  const containerObservers = new Map<HTMLElement, MutationObserver>();
  const seenParents = new WeakSet<HTMLElement>();

  const observeContainer = (container: HTMLElement) => {
    apply(container);
    if (containerObservers.has(container)) return;
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.target instanceof HTMLElement) apply(record.target);
      });
    });
    observer.observe(container, {
      childList: true,
      attributes: true,
      attributeFilter: ["role"],
    });
    containerObservers.set(container, observer);
  };

  const unobserveContainer = (container: HTMLElement) => {
    const observer = containerObservers.get(container);
    if (observer) {
      observer.disconnect();
      containerObservers.delete(container);
    }
  };

  // (Re-)scan the grid for empty-prone rowgroup containers, fix each, and
  // observe any we have not wired yet. AG Grid can DESTROY and RECREATE these
  // container nodes (view-mode switch, column changes) without re-firing
  // onGridReady, which would otherwise leave a fresh empty rowgroup unobserved.
  // We therefore also watch each container's parent (a stable viewport that is
  // NOT replaced on scroll) with a childList observer, so a replaced container
  // is re-fixed and re-observed. Parent childList fires only when the container
  // itself is added/removed — not on row virtualization — so this stays cheap.
  const wire = () => {
    hostEl
      .querySelectorAll<HTMLElement>(EMPTY_PRONE_ROWGROUP_SELECTOR)
      .forEach((container) => {
        observeContainer(container);
        const parent = container.parentElement;
        if (parent && !seenParents.has(parent)) {
          seenParents.add(parent);
          parentObserver.observe(parent, { childList: true });
        }
      });
  };

  // On a parent childList change, stop observing any rowgroup container AG
  // Grid removed (so detached nodes are not kept under observation), then
  // (re-)wire whatever is currently attached.
  const parentObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.removedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(EMPTY_PRONE_ROWGROUP_SELECTOR)) {
          unobserveContainer(node);
        }
        node
          .querySelectorAll<HTMLElement>(EMPTY_PRONE_ROWGROUP_SELECTOR)
          .forEach(unobserveContainer);
      });
    });
    wire();
  });

  // AG Grid renders `.ag-full-width-container` as part of its skeleton at init,
  // so its absence means this is not an AG Grid host — nothing to wire.
  if (!hostEl.querySelector(".ag-full-width-container")) return null;

  wire();

  return {
    disconnect() {
      containerObservers.forEach((observer) => observer.disconnect());
      containerObservers.clear();
      parentObserver.disconnect();
    },
  };
}

// Unique per-grid prefix for the ids we stamp onto row-header cells, so two
// grids on the same page never collide on `id`/`aria-describedby` IDREFs.
let rowHeaderAssociationSeq = 0;

/**
 * Associate each data cell with its row header for screen readers
 * (WCAG 1.3.1 Info and Relationships / 4.1.2 Name, Role, Value).
 *
 * The CT Plan grid marks its pinned-left label column (parameter/metric names
 * such as "Contacts Received (Forecast)") as `role="rowheader"` via
 * `colDef.cellAriaRole`, and its date columns as `role="columnheader"`. JAWS
 * associates a COLUMN header with a data cell by matching `aria-colindex`
 * (across the separate header container), so it already announces the date. It
 * associates a ROW header only with cells in the SAME DOM `role="row"`, however
 * — but AG Grid renders pinned columns in a physically separate row/container
 * from the center (and pinned-right) data cells. The center data cell's row
 * therefore contains no `rowheader`, so JAWS never announces the metric name,
 * leaving the row context missing (e.g. it reads "Apr 2021, 300,000, column 2"
 * instead of "Contacts Received (Forecast), Apr 2021, 300,000").
 *
 * This bridges the split: each row-header cell is given a stable id (keyed on
 * its logical `aria-rowindex`), and every data cell (`role="gridcell"`) sharing
 * that row index gets an `aria-describedby` pointing at it, so JAWS announces
 * the row header alongside the column header and value regardless of which
 * pinned/center container the cell lives in. Writing `id`/`aria-describedby`
 * only when the value changes keeps it idempotent.
 *
 * AG Grid virtualizes rows and reuses row elements (updating their
 * `aria-rowindex`) on scroll/view changes and may apply `role` late, so this is
 * wired to a MutationObserver rather than run once. `id`/`aria-describedby` are
 * excluded from the observed attribute filter, so our own writes never
 * re-trigger the observer. Returns a disconnectable so the caller can tear it
 * down on destroy.
 *
 * Pass the component host element from `onGridReady` (ElementRef.nativeElement).
 */
export function associateGridRowHeaders(
  hostEl: HTMLElement | null | undefined
): { disconnect(): void } | null {
  if (!hostEl) return null;
  // AG Grid renders its root as part of the skeleton at init; its absence
  // means this is not an AG Grid host, so there is nothing to associate.
  if (!hostEl.querySelector(".ag-root")) return null;

  const idPrefix = `esp-grid-rowheader-${++rowHeaderAssociationSeq}-r`;

  const rowIndexOf = (cell: Element): string | null => {
    const row = cell.closest('[role="row"]');
    return row ? row.getAttribute("aria-rowindex") : null;
  };

  const apply = () => {
    // Map each logical row index to its row-header cell id, stamping a stable
    // id (keyed on the row index) on the row-header cell if it is missing/stale.
    const idByRowIndex = new Map<string, string>();
    hostEl.querySelectorAll<HTMLElement>('[role="rowheader"]').forEach((rh) => {
      const idx = rowIndexOf(rh);
      if (!idx) return;
      const wantedId = idPrefix + idx;
      if (rh.id !== wantedId) rh.id = wantedId;
      idByRowIndex.set(idx, wantedId);
    });

    // Point every data cell at the row-header for its logical row, preserving
    // any pre-existing describedby tokens that are not ours.
    hostEl.querySelectorAll<HTMLElement>('[role="gridcell"]').forEach((cell) => {
      const row = cell.closest('[role="row"]');
      const idx = row ? row.getAttribute("aria-rowindex") : null;
      // When the parameter column is NOT pinned (flat CT view), the row-header
      // cell lives in the SAME DOM row as the data cells, so JAWS associates it
      // natively. Adding aria-describedby on top of that would make JAWS
      // announce the row header a SECOND time (it is also part of each cell's
      // accessible name), so only bridge when the header sits in a physically
      // separate (pinned) row — otherwise remove any stale token we added.
      const headerInSameRow = !!row?.querySelector('[role="rowheader"]');
      const rhId = idx && !headerInSameRow ? idByRowIndex.get(idx) : undefined;
      const existing = cell.getAttribute("aria-describedby");
      const tokens = existing ? existing.split(/\s+/).filter(Boolean) : [];
      const preserved = tokens.filter((t) => !t.startsWith(idPrefix));
      const next = (rhId ? [...preserved, rhId] : preserved).join(" ");
      if (next) {
        if (existing !== next) cell.setAttribute("aria-describedby", next);
      } else if (existing !== null) {
        cell.removeAttribute("aria-describedby");
      }
    });
  };

  apply();

  // React to cell virtualization (childList) and to AG Grid reusing rows
  // (aria-rowindex) or applying role late. `id`/`aria-describedby` are NOT in
  // the filter, so our own writes never re-trigger this observer.
  const observer = new MutationObserver(apply);
  observer.observe(hostEl, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["aria-rowindex", "role"],
  });

  return {
    disconnect() {
      observer.disconnect();
    },
  };
}

// Unique per-grid prefix for the ids we stamp when deduping pinned rows, so two
// grids on the same page never collide on `id`/`aria-describedby` IDREFs. The
// shared base is used both to build a per-grid prefix AND to strip ANY of our
// previous tokens (from an earlier prefix/seq) so re-runs never leave a stale
// IDREF behind.
const PINNED_ROWHEADER_ID_BASE = "esp-grid-pinned-rowheader-";
let pinnedRowDedupeSeq = 0;

/**
 * Collapse AG Grid's duplicated pinned rows in the accessibility tree so screen
 * readers report the correct row count (WCAG 1.3.1 Info and Relationships /
 * 4.1.2 Name, Role, Value).
 *
 * When a column is pinned (e.g. the Learning Curve grid pins its "Parameter"
 * row-header column), AG Grid renders EVERY logical row twice: once in the
 * `.ag-pinned-left-*` container and once in the `.ag-center-*` / header
 * container. Both DOM rows carry `role="row"` with the SAME `aria-rowindex`, so
 * although the grid's `aria-rowcount` is correct (e.g. 3), JAWS counts the
 * physical `role="row"` elements (e.g. 6) and announces "6 rows" for a 3-row
 * table.
 *
 * This keeps the CENTER (and center header) rows as the single semantic rows
 * and neutralises only the duplicate PINNED row WRAPPERS to
 * `role="presentation"`, so the AT counts each logical row once. The pinned
 * cell itself (the "Parameter" row/column header) KEEPS its
 * `rowheader`/`columnheader` role and its `aria-colindex`, and the matching
 * center row ADOPTS it via `aria-owns`. In the accessibility tree the pinned
 * header cell therefore becomes a real child of the single semantic center row,
 * so JAWS announces it with the correct position — e.g. landing on the pinned
 * "AHT (seconds)" cell reads "AHT (seconds), row 2, column 1" (ESP-18588) —
 * while the row count stays correct (ESP-18571). `aria-owns` (not
 * `aria-describedby`) is used so the header is exposed once, as a genuine
 * row/column header at its own grid position, rather than being appended as a
 * description to every data cell.
 *
 * Pinned/center rows are matched by CLASS (`.ag-pinned-left-cols-container`,
 * `.ag-row`, `.ag-cell`) and paired by `aria-rowindex`, so the association
 * survives our own `role="presentation"` writes on the wrappers and re-runs are
 * idempotent. AG Grid virtualizes and reuses rows, so this is wired to a
 * MutationObserver; the `role`/`aria-rowindex` writes are guarded to only fire
 * on a real change (and `id`/`aria-owns` are outside the observed filter) so the
 * observer quiesces. Returns a disconnectable so the caller can tear it down on
 * destroy. Pass the component host element from `onGridReady`
 * (ElementRef.nativeElement).
 */
export function dedupePinnedGridRows(
  hostEl: HTMLElement | null | undefined
): { disconnect(): void } | null {
  if (!hostEl) return null;
  // AG Grid renders its root as part of the skeleton at init; its absence
  // means this is not an AG Grid host, so there is nothing to dedupe.
  if (!hostEl.querySelector(".ag-root")) return null;

  const idPrefix = `${PINNED_ROWHEADER_ID_BASE}${++pinnedRowDedupeSeq}-r`;

  const apply = () => {
    // 1. Stamp a stable id on each pinned row/column-header cell (keyed on row
    //    index) and KEEP its semantic role. The pinned-left/right cols and
    //    header containers hold ONLY the row-header column, so the single
    //    `.ag-cell` / `.ag-header-cell` in each pinned row IS that row's header.
    //    Match by class so this still works after we neutralise the wrapper row
    //    role below.
    const idByRowIndex = new Map<string, string>();
    hostEl
      .querySelectorAll<HTMLElement>(
        ".ag-pinned-left-cols-container .ag-row, .ag-pinned-right-cols-container .ag-row, " +
          ".ag-pinned-left-header .ag-header-row, .ag-pinned-right-header .ag-header-row"
      )
      .forEach((row) => {
        const idx = row.getAttribute("aria-rowindex");
        const cell = row.querySelector<HTMLElement>(".ag-cell, .ag-header-cell");
        if (!idx || !cell) return;
        const wantedId = idPrefix + idx;
        if (cell.id !== wantedId) cell.id = wantedId;
        idByRowIndex.set(idx, wantedId);
      });

    // 2. Have each center row ADOPT the pinned header cell for its logical row
    //    via `aria-owns`, so the pinned rowheader/columnheader becomes a child
    //    of the single semantic (center) row in the accessibility tree — JAWS
    //    then announces it as a real header cell at its own position (row N,
    //    column 1). Preserve any pre-existing aria-owns tokens that are not ours
    //    and strip ANY of our previous tokens (from an earlier prefix/seq if the
    //    grid is re-deduped) so a re-run never leaves a stale IDREF behind.
    hostEl
      .querySelectorAll<HTMLElement>(
        ".ag-center-cols-container .ag-row, .ag-header-container .ag-header-row"
      )
      .forEach((row) => {
        const idx = row.getAttribute("aria-rowindex");
        const rhId = idx ? idByRowIndex.get(idx) : undefined;
        const existing = row.getAttribute("aria-owns");
        const tokens = existing ? existing.split(/\s+/).filter(Boolean) : [];
        const preserved = tokens.filter(
          (t) => !t.startsWith(PINNED_ROWHEADER_ID_BASE)
        );
        const next = (rhId ? [...preserved, rhId] : preserved).join(" ");
        if (next) {
          if (existing !== next) row.setAttribute("aria-owns", next);
        } else if (existing !== null) {
          row.removeAttribute("aria-owns");
        }
      });

    // 3. Neutralise ONLY the duplicate pinned row WRAPPERS (header + body) to
    //    role="presentation" so the AT counts each logical row once. The pinned
    //    header CELLS deliberately keep their rowheader/columnheader role: they
    //    are adopted into the center row via `aria-owns` (step 2), so they are
    //    valid header cells of that row in the accessibility tree and announce
    //    the correct row/column position, while the duplicate wrapper row they
    //    physically live in is no longer a counted row.
    const pinnedRowSelector =
      ".ag-pinned-left-cols-container [role=\"row\"], " +
      ".ag-pinned-right-cols-container [role=\"row\"], " +
      ".ag-pinned-left-header [role=\"row\"], " +
      ".ag-pinned-right-header [role=\"row\"]";

    hostEl
      .querySelectorAll<HTMLElement>(pinnedRowSelector)
      .forEach((row) => {
        if (row.getAttribute("role") !== "presentation") {
          row.setAttribute("role", "presentation");
        }
      });
  };

  apply();

  // React to cell virtualization (childList) and to AG Grid reusing rows
  // (aria-rowindex) or re-applying role. `id`/`aria-owns` are NOT in the
  // filter and the `role` writes are guarded, so our own writes settle quickly.
  const observer = new MutationObserver(apply);
  observer.observe(hostEl, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["aria-rowindex", "role"],
  });

  return {
    disconnect() {
      observer.disconnect();
    },
  };
}

/**
 * Synchronise `aria-hidden` on the Plan rollup data rows in the AG Grid
 * floating-top panel (WCAG 1.3.1 / WCAG 4.1.2 — Name, Role, Value).
 *
 * When the "Plan rollup" group is collapsed its child data rows are visually
 * hidden via CSS (`max-height` on `.ag-floating-top`), but they remain in the
 * DOM. JAWS and other screen readers therefore navigate into the hidden rows
 * as if they were visible, because there is nothing in the accessibility tree
 * to signal that they are collapsed content.
 *
 * This function applies `aria-hidden="true"` to every `.ag-row` inside
 * `.ag-floating-top` EXCEPT the first one (index 0), which is the rollup
 * header row itself and must remain announced. When expanded the attribute is
 * removed so the rows are accessible again.
 *
 * Call it immediately after toggling `_isRollupCollapsed`, passing the
 * component host element (ElementRef.nativeElement) and the new collapsed state.
 */
export function setRollupCollapsedAriaHidden(
  hostEl: HTMLElement | null | undefined,
  isCollapsed: boolean
): void {
  if (!hostEl) return;
  const floatingTop = hostEl.querySelector<HTMLElement>('.ag-floating-top');
  if (!floatingTop) return;
  const rows = floatingTop.querySelectorAll<HTMLElement>('[role="row"]');
  // Row 0 is the rollup header — always keep it announced.
  rows.forEach((row, index) => {
    if (index === 0) return;
    if (isCollapsed) {
      row.setAttribute('aria-hidden', 'true');
    } else {
      row.removeAttribute('aria-hidden');
    }
  });
}

/**
 * Announce a grid's row-level activation instruction ONCE when focus enters
 * the grid (WCAG 1.3.1 Info and Relationships / 3.3.2 Labels or Instructions).
 *
 * These landing-page grids open a details view (Plan Details / Import Forecast
 * Log) from ANY cell in a row — a deliberate row-level action for both mouse
 * and keyboard. Exposing that action on every cell would make a screen reader
 * repeat the same hint for each gridcell. Instead, point the grid's
 * `aria-describedby` at a single visually-hidden instruction element (e.g.
 * "Plan Details grid. Select any cell in the row and press Enter to open Plan
 * Details."). Screen readers read it once on grid entry, while each cell still
 * announces only its column header + value.
 *
 * Pass the component host element (ElementRef.nativeElement) from
 * `onGridReady` and the id of the hidden instruction element in the template.
 */
export function setGridActivationDescription(
  hostEl: HTMLElement | null | undefined,
  descriptionElementId: string
): void {
  if (!hostEl || !descriptionElementId) return;
  const gridEl = hostEl.querySelector('[role="grid"], [role="treegrid"]');
  if (!gridEl) return;
  gridEl.setAttribute("aria-describedby", descriptionElementId);
}

/**
 * Give a grid a descriptive caption / accessible name
 * (WCAG 1.3.1 Info and Relationships / 4.1.2 Name, Role, Value).
 *
 * Screen readers announce a grid's accessible name when focus enters it and
 * when users jump to it with the "T" (table) quick navigation key. AG Grid
 * does not set one, so the table is announced with no caption. Setting an
 * `aria-label` (e.g. the page heading "Manage Plans" / "Import Forecasts") on
 * the grid root gives the table a caption that JAWS reads on grid entry and on
 * "T" navigation. This is distinct from `aria-describedby` (the row-level
 * activation hint), which is announced as a description, not the caption.
 *
 * Pass the component host element from `onGridReady` (ElementRef.nativeElement)
 * and the caption text.
 */
export function setGridAccessibleName(
  hostEl: HTMLElement | null | undefined,
  name: string | null | undefined
): void {
  if (!hostEl || !name) return;
  const gridEl = hostEl.querySelector('[role="grid"], [role="treegrid"]');
  if (!gridEl) return;
  gridEl.setAttribute("aria-label", name);
}

/**
 * Keep Tab / Shift+Tab focus order working between the group-row expand/collapse
 * buttons in the Plan Detail grid (WCAG 2.1.1 Keyboard, 2.4.3 Focus Order).
 *
 * Every group row — the Plan rollup full-width row and each MU group row —
 * renders its own `<button class="ag-group-toggle">` inside the
 * `<mu-group-header-v2>` renderer (the grid uses a full groupRowRenderer, so the
 * DOM is flat). Those real buttons already carry `role="button"`,
 * `aria-expanded` and a localised `aria-label` from the component template and
 * activate on Enter/Space natively, so no ARIA stamping is needed here. The
 * problem this solves is that AG Grid hijacks Tab on group/full-width rows and
 * can drop focus into nowhere. A capture-phase Tab handler on the grid root
 * intercepts Tab before AG Grid's own handlers and moves focus to the adjacent
 * toggle button in visual order, letting native Tab leave the grid at the
 * first/last button.
 *
 * The `expandLabel`/`collapseLabel` params are retained only for call-site
 * compatibility and are no longer used.
 *
 * Returns a disconnectable so the caller can tear down the listener on destroy.
 * Pass the component host element from `onGridReady` (ElementRef.nativeElement).
 */
export function patchMuGroupRowToggleAria(
  hostEl: HTMLElement | null | undefined,
  // Retained for call-site compatibility. The <mu-group-header-v2> button now
  // supplies its own localised aria-label/aria-expanded, so this utility no
  // longer stamps ARIA — it only manages Tab focus order between the buttons.
  _expandLabel?: (groupName: string) => string,
  _collapseLabel?: (groupName: string) => string
): { disconnect(): void } | null {
  if (!hostEl) return null;
  const gridRoot = hostEl.querySelector<HTMLElement>('.ag-root');
  if (!gridRoot) return null;

  // Every group row — the Plan rollup full-width row and each MU group row —
  // renders its own <button class="ag-group-toggle"> inside the
  // <mu-group-header-v2> renderer (the grid uses a full groupRowRenderer, so
  // the DOM is flat: div.ag-row > mu-group-header-v2 > button). That real
  // <button> is the single keyboard/screen-reader focus target: it carries
  // role="button", aria-expanded and a localised aria-label from the component
  // template, is natively focusable, and activates on Enter/Space natively.
  // All this utility needs to do is keep Tab / Shift+Tab moving between those
  // buttons — AG Grid otherwise hijacks Tab on group/full-width rows and drops
  // focus into nowhere.
  const TOGGLE_SELECTOR = 'mu-group-header-v2 button.ag-group-toggle';

  // All visible toggle buttons, in visual (top-to-bottom, then left-to-right)
  // order, so Tab flows Plan rollup -> Eastern America -> Western America, etc.
  // Visible = it renders a box. getClientRects() is more reliable than
  // offsetParent here: the Plan rollup button lives in AG Grid's pinned
  // `ag-floating-top-full-width-container`, whose transform/positioning can
  // make offsetParent null even though the button is on screen — using
  // offsetParent would wrongly drop the rollup button from the tab ring and
  // let focus fall through to nowhere.
  const orderedToggles = (): HTMLElement[] =>
    Array.from(gridRoot.querySelectorAll<HTMLElement>(TOGGLE_SELECTOR))
      .filter(
        (el) =>
          el.getClientRects().length > 0 && !el.classList.contains('ag-hidden')
      )
      .sort((a, b) => {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        return ra.top - rb.top || ra.left - rb.left;
      });

  // Intercept Tab in the CAPTURE phase on the grid root — before AG Grid's own
  // bubble-phase row handlers run — and move focus to the adjacent toggle
  // button ourselves. On the first (Shift+Tab) / last (Tab) button we leave the
  // event alone so native Tab advances to the next natural control
  // (WCAG 2.1.1 Keyboard, 2.4.3 Focus Order).
  const onGridRootTabCapture = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const active = document.activeElement as HTMLElement | null;
    // Focus may be on the toggle button itself OR on the full-width / group
    // ROW that AG Grid parks focus on (the Plan rollup pinned row and the MU
    // group rows live in two separate containers —
    // `ag-floating-top-full-width-container` and `ag-full-width-container` —
    // and AG Grid focuses the ROW div, not our inner button). Resolve the
    // toggle from either case.
    const onToggle = active?.closest<HTMLElement>(TOGGLE_SELECTOR) ?? null;
    const current =
      onToggle ?? active?.querySelector<HTMLElement>(TOGGLE_SELECTOR) ?? null;
    if (!current) return; // focus is not on / within a group toggle — leave Tab alone
    // Focus parked on the group/full-width ROW element itself (AG Grid does
    // this when the user tabs onto the row from a cell — in either direction).
    // The row is not an interactive stop of its own: from it, Tab AND
    // Shift+Tab must step INTO the row's expand/collapse button, otherwise
    // the toggle is skipped entirely in that direction (ESP-18579). The ring
    // below only ever advances from the BUTTONS themselves.
    if (!onToggle) {
      event.preventDefault();
      event.stopPropagation();
      current.focus();
      return;
    }
    const toggles = orderedToggles();
    const idx = toggles.indexOf(current);
    if (idx === -1) return;
    // Focus is on a group toggle: always stop the event reaching AG Grid's own
    // Tab handler, which would otherwise park focus on a row div and reintroduce
    // the "Tab drops focus" bug. At the ends of the toggle ring we still skip
    // preventDefault so native Tab can leave the grid to the next/prev control.
    event.stopPropagation();
    const next = toggles[idx + (event.shiftKey ? -1 : 1)];
    if (!next) return; // first/last toggle — let native Tab leave the group set
    event.preventDefault();
    next.focus();
  };

  gridRoot.addEventListener('keydown', onGridRootTabCapture, true);

  // AG Grid makes the full-width / group ROW div itself focusable (tabindex=-1)
  // and parks focus on it when the user Tabs / Shift+Tabs onto a group row FROM
  // A DATA CELL. At keydown time focus is still on the cell, so the handler
  // above cannot catch it; AG Grid then lands on the bare row div — a redundant,
  // non-interactive tab stop. Bounce focus off that row div straight into its
  // expand/collapse button so the BUTTON is the single stop and the tab order
  // is preserved (WCAG 2.4.3 Focus Order).
  const onGridRootFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    // Only act when focus landed on a group/full-width ROW element itself, not
    // on the toggle button (or anything inside it).
    if (target.closest(TOGGLE_SELECTOR)) return;
    const role = target.getAttribute?.('role');
    if (role !== 'row') return;
    const toggle = target.querySelector<HTMLElement>(TOGGLE_SELECTOR);
    if (toggle) toggle.focus();
  };

  gridRoot.addEventListener('focusin', onGridRootFocusIn, true);

  return {
    disconnect() {
      gridRoot.removeEventListener('keydown', onGridRootTabCapture, true);
      gridRoot.removeEventListener('focusin', onGridRootFocusIn, true);
    },
  };
}

/** 
* AG Grid keyboard accessibility utility (WCAG 2.2 SC 2.1.1 Keyboard).
 *
 * Pass this function as `suppressKeyboardEvent` on any column definition
 * whose cell contains an interactive element activated via the cell itself.
 *
 * - Tab   → NOT suppressed (returns false) so AG Grid moves focus to the
 *           next cell. The interactive child must use tabindex="-1" so the
 *           browser does not insert it into the tab sequence.
 * - Enter / Space → when the cell has focus, programmatically click the
 *           interactive child inside it (delete icon, etc).
 *
*/

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function tabSuppressKeyboardEventForFocusableChildren(params: any): boolean {
  if (!params || !params.event) return false;
  const key: string = params.event.key;

  // Let AG Grid handle Tab natively — moves focus cell-to-cell.
  if (key === "Tab") {
    return false;
  }

  // Enter/Space on the cell: trigger the focusable child (e.g. delete icon).
  if (key === "Enter" || key === " ") {
    const active = document.activeElement as HTMLElement | null;
    const role = active?.getAttribute("role");

    if (role === "gridcell") {
      // Match both native <button> (e.g. the delete icon in
      // GridItemActionComponent, which is tabindex="-1" so the CELL is the
      // single Tab stop) and elements with an explicit role="button".
      const btn = active?.querySelector<HTMLElement>('button, [role="button"]');
      if (btn) {
        // Prevent the browser default (Space scrolls the viewport) so the
        // grid does not jump when the delete popover is opened via keyboard.
        params.event.preventDefault();
        setTimeout(() => btn.click());
        return true;
      }
    }
  }

  return false;
}

/**
 * ARIA grid keyboard pattern for the plan-detail parameter (row-header) cell
 * that hosts the inline "edit row" pencil button (WCAG 2.1.1 Keyboard,
 * 4.1.2 Name, Role, Value).
 *
 * The parameter cell is a role="rowheader" cell. AG Grid keeps the whole grid
 * a single Tab stop, so the non-tabbable pencil <button> inside the cell can
 * never receive focus on its own; instead the user arrow-navigates onto the
 * row-header cell and the cell's accessible name announces that the row can be
 * edited. Pressing Enter on the focused row-header cell must then activate the
 * pencil (start editing the row) exactly as the announced instructions tell the
 * user ("Press Enter to edit this row"). AG Grid would otherwise swallow the
 * key, so we intercept it here (BEFORE AG Grid acts) and click the cell's
 * `.row-edit-button` child. Only Enter is handled (not Space): Space on a grid
 * cell is reserved by AG Grid for range/row selection, and the on-screen and
 * screen-reader instructions only mention Enter, so activating on Space too
 * would both hijack selection and contradict the announced instructions.
 *
 * Use as the `suppressKeyboardEvent` callback on the parameter column def.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function suppressRowHeaderEditButtonKeyboardEvent(params: any): boolean {
  if (!params || !params.event) {
    return false;
  }
  const key: string = params.event.key;
  if (key !== "Enter") {
    return false;
  }
  const active = document.activeElement as HTMLElement | null;
  const role = active?.getAttribute("role");
  if (role !== "rowheader" && role !== "gridcell") {
    return false;
  }
  const btn = active?.querySelector<HTMLElement>(".row-edit-button");
  if (!btn) {
    return false;
  }
  // Prevent AG Grid's own Enter handling (which would start editing the
  // non-editable row-header cell) so only the pencil activation runs.
  params.event.preventDefault();
  setTimeout(() => btn.click());
  return true;
}

/**
 * ARIA grid keyboard pattern — Home / End / Ctrl+Home / Ctrl+End
 * (WCAG 2.1 SC 2.1.1 Keyboard, ARIA Authoring Practices Grid pattern).
 *
 * AG Grid's built-in Home/End move focus to the first/last cell of the
 * entire grid. The ARIA grid pattern requires:
 *   Home        → first cell of the current row
 *   End         → last cell of the current row
 *   Ctrl + Home → first cell of the entire grid
 *   Ctrl + End  → last cell of the entire grid
 *
 * Use `suppressHomeEndKeyboardEvent` as the `suppressKeyboardEvent` callback
 * in `defaultColDef`. This intercepts BEFORE AG Grid acts on the key.
 * Wiring it to `(keydown)` on the host element does NOT work because that
 * fires after AG Grid has already moved focus.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function handleGridHomeEndKeys(api: any, event: KeyboardEvent): void {
  const key = event.key;
  if (key !== "Home" && key !== "End") return;

  // Do nothing while a cell editor is open.
  if (api.getEditingCells?.()?.length) return;

  const focusedCell = api.getFocusedCell();
  if (!focusedCell) return;

  const allColumns: any[] = api.getAllDisplayedColumns();
  if (!allColumns || allColumns.length === 0) return;

  // Guard: empty grid (e.g. after filtering) — no valid row to navigate to.
  const rowCount: number = api.getDisplayedRowCount();
  if (rowCount === 0) return;

  event.preventDefault();

  const ctrl = event.ctrlKey || event.metaKey;
  const isHome = key === "Home";

  const targetCol = isHome ? allColumns[0] : allColumns[allColumns.length - 1];
  const targetRow = ctrl
    ? (isHome ? 0 : rowCount - 1)
    : focusedCell.rowIndex;

  api.ensureIndexVisible(targetRow);
  api.ensureColumnVisible(targetCol);
  api.setFocusedCell(targetRow, targetCol);
}

/**
 * Use this as `suppressKeyboardEvent` in `defaultColDef`.
 * Returning `true` prevents AG Grid from applying its own Home/End logic;
 * our correct per-row / per-grid navigation is applied instead.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function suppressHomeEndKeyboardEvent(params: any): boolean {
  const key = params?.event?.key;
  if (key !== "Home" && key !== "End") return false;
  handleGridHomeEndKeys(params.api, params.event);
  return true;
}

/**
 * Elements that participate in the sequential (Tab) focus order.
 *
 * Each native-focusable selector also excludes elements explicitly removed from
 * the tab order via a negative `tabindex` (`tabindex="-1"`, `-2`, ...), so Tab
 * cannot land focus on a non-tabbable element when leaving the grid.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex^="-"])',
  'button:not([disabled]):not([tabindex^="-"])',
  'input:not([disabled]):not([tabindex^="-"])',
  'select:not([disabled]):not([tabindex^="-"])',
  'textarea:not([disabled]):not([tabindex^="-"])',
  '[tabindex]:not([tabindex^="-"])',
].join(",");

function isVisibleFocusable(el: HTMLElement): boolean {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

/**
 * Whether a control inside a grid can actually take keyboard focus right now.
 *
 * Stricter than `isVisibleFocusable` in two ways that matter for grid controls:
 *   - `visibility: hidden` / `display: none`. Bootstrap's `.invisible` is
 *     `visibility: hidden`, which leaves `offsetWidth`/`offsetParent` intact, so
 *     a check built on those alone happily "focuses" an invisible control. The
 *     empty learning-curve group row's expand toggle is exactly that case and is
 *     only saved today by also carrying `[disabled]`.
 *   - explicitly disabled, via the `disabled` attribute or `aria-disabled`.
 *
 * A negative `tabindex` is also rejected: AG Grid renders plenty of natively
 * focusable elements with `tabindex="-1"` and drives focus itself, so they are
 * not real Tab stops.
 */
export function canTakeGridFocus(el: HTMLElement): boolean {
  if (!el) return false;
  if (el.getAttribute("tabindex") === "-1") return false;
  if ((el as HTMLButtonElement).disabled === true) return false;
  if (el.getAttribute("aria-disabled") === "true") return false;
  if (el.offsetParent === null && el.getClientRects().length === 0) return false;
  const style = el.ownerDocument?.defaultView?.getComputedStyle(el);
  if (style && (style.visibility === "hidden" || style.display === "none")) {
    return false;
  }
  return true;
}

/**
 * Put focus back on a full-width row's control after AG Grid re-homes it onto
 * the row container (WCAG 2.1.1 Keyboard, 2.4.3 Focus Order).
 *
 * `RowCtrl.onFullWidthRowFocused` focuses the ROW element itself on a later tick
 * whenever the grid's focused row index matches it. The row is `tabindex="-1"`
 * and paints no focus ring, so whatever landed on one of the row's own controls
 * (the learning-curve title, "Add Class") silently loses focus and the user sees
 * nothing happen.
 *
 * The re-assert runs on the next frame and ONLY for that specific steal: focus
 * has to have landed on the row container itself, or been dropped to
 * `<body>`/nothing. A move to any other real control is something the user (or
 * another handler) asked for and is left strictly alone — otherwise this would
 * drag focus back out of the next control on every keystroke.
 *
 * `dest` may be any element; nothing happens unless it lives in a full-width row.
 */
function reassertFocusOnFullWidthRowControl(dest: HTMLElement | null | undefined): void {
  if (!dest || typeof dest.closest !== "function") return;
  const fullWidthRow = dest.closest(".ag-full-width-row") as HTMLElement | null;
  if (!fullWidthRow) return;
  const doc = dest.ownerDocument;
  const raf =
    doc?.defaultView?.requestAnimationFrame?.bind(doc.defaultView) ??
    ((cb: FrameRequestCallback) => setTimeout(() => cb(0)) as unknown as number);
  raf(() => {
    // Gone from the DOM (row re-rendered) — nothing to restore.
    if (!dest.isConnected) return;
    const active = doc?.activeElement as HTMLElement | null;
    // Focus is still where we put it.
    if (active === dest || (active && dest.contains(active))) return;
    // Only reclaim it from AG Grid's own row-container steal (or from focus
    // having been dropped entirely). Anything else is a deliberate move.
    const stolenByRow = active === fullWidthRow;
    const dropped = !active || active === doc?.body;
    if (!stolenByRow && !dropped) return;
    dest.focus({ preventScroll: true });
  });
}

/**
 * Move keyboard focus to the first focusable element immediately AFTER the grid
 * (or BEFORE it when `backwards`), scoped to the nearest dialog/modal container
 * (falling back to the document body). Returns `true` when focus is moved.
 *
 * Anti-trap: when there is NO focusable element after the grid (forward) or
 * before it (backward) — e.g. the grid is the LAST interactive content on the
 * page (the New Hire Training Class grid has controls only above it and nothing
 * focusable below) — focus WRAPS to the boundary control in scope instead of
 * staying put. Without this the caller's unconditional `preventDefault()` used
 * to trap focus on the cell the user tabbed onto (reported as "focus is stuck
 * on the first column header"). Returns `false` only when the grid is the sole
 * focusable region in scope, so the caller can then leave the native Tab alone.
 */
function moveFocusOutsideGrid(gridRoot: HTMLElement, backwards: boolean): boolean {
  const doc = gridRoot.ownerDocument || document;
  const scope =
    (gridRoot.closest('[role="dialog"], .modal-content, .modal') as HTMLElement) ||
    doc.body;
  if (!scope) return false;

  const focusables = Array.from(
    scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (el) => !gridRoot.contains(el) && !el.contains(gridRoot) && isVisibleFocusable(el)
  );
  // Nothing focusable outside the grid at all → there is nowhere to send focus,
  // so report failure and let the caller keep the browser's native Tab.
  if (!focusables.length) return false;

  let target: HTMLElement | null = null;
  for (const el of focusables) {
    const pos = gridRoot.compareDocumentPosition(el);
    if (!backwards) {
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
        target = el; // first focusable after the grid
        break;
      }
    } else if (pos & Node.DOCUMENT_POSITION_PRECEDING) {
      target = el; // keep the closest focusable before the grid
    } else if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
      break;
    }
  }

  // No control on the expected side of the grid → WRAP so Tab still leaves the
  // grid: forward wraps to the first focusable in scope, backward to the last.
  if (!target) {
    target = backwards ? focusables[focusables.length - 1] : focusables[0];
  }

  target.focus();
  return true;
}

/**
 * Move keyboard focus OUT of the grid contained within `hostEl` to the next
 * interactive control after it (or before it when `backwards`), following the
 * same exit model as `suppressGridTabToExit` (ARIA "grid" pattern / WCAG 2.1.1
 * Keyboard, 2.1.2 No Keyboard Trap).
 *
 * This is the escape hatch for CUSTOM CELL EDITORS. AG Grid's per-column
 * `suppressKeyboardEvent` runs on the cell container, but a cell editor's own
 * `keydown` handler can `stopPropagation()` (so the grid never sees Tab) and
 * instead call `api.tabToNextCell()` — which cycles editor-to-editor and never
 * leaves the grid, re-introducing the exact keyboard trap the exit model
 * removes ("focus was stuck when pressing Tab in the editable cell"). An editor
 * wired to the exit model calls this after `api.stopEditing()` so a single Tab
 * commits the value and moves focus past the grid. `hostEl` is any ancestor of
 * the grid (e.g. the component host); the `.ag-root-wrapper` is located within
 * it. Returns `true` when focus was moved.
 */
export function focusOutOfGrid(
  hostEl: HTMLElement | null | undefined,
  backwards: boolean
): boolean {
  const gridRoot =
    hostEl && typeof hostEl.querySelector === "function"
      ? (hostEl.querySelector(".ag-root-wrapper") as HTMLElement | null)
      : null;
  return gridRoot ? moveFocusOutsideGrid(gridRoot, backwards) : false;
}

/**
 * Make Tab move focus OUT of the grid instead of cell-to-cell
 * (ARIA Authoring Practices "Grid" pattern / WCAG 2.1.1 Keyboard).
 *
 * For a non-interactive (read-only) data grid the ARIA grid pattern says cells
 * are reached with the Arrow keys, and a SINGLE Tab moves focus past the whole
 * grid to the next interactive control. AG Grid's default instead steps Tab
 * through every cell, which is inconsistent and confusing alongside the arrow
 * keys.
 *
 * Returning `true` alone is NOT enough: it only tells AG Grid to skip its own
 * handling, but AG Grid's tab guards still route focus to the next cell, so Tab
 * never actually leaves the grid (ESP-18433 reopened). This handler therefore
 * ALSO moves focus to the next interactive control after the grid (or before it
 * for Shift+Tab) and prevents the default, so a single Tab reliably exits.
 * Arrow keys are not suppressed, so in-grid navigation is unchanged.
 *
 * Use as the `suppressKeyboardEvent` callback in `defaultColDef`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function suppressGridTabToExit(params: any): boolean {
  const event = params?.event;
  if (!event || event.key !== "Tab") return false;

  const target = event.target as HTMLElement | undefined;
  const gridRoot =
    target && typeof target.closest === "function"
      ? (target.closest(".ag-root-wrapper") as HTMLElement | null)
      : null;

  // Move focus to the first interactive control just outside the grid (or wrap
  // to the boundary control when the grid is the last/first interactive region
  // — see moveFocusOutsideGrid). We ONLY prevent the browser's native Tab when
  // focus was actually moved: preventing it stops AG Grid's tab guards routing
  // Tab back into the next cell, but doing so UNCONDITIONALLY trapped focus on
  // the current cell whenever there was nothing focusable to move to (reported
  // as "focus is stuck on the first column header"). If moveFocusOutsideGrid
  // finds no destination we leave the native Tab alone so focus can still
  // escape. Arrow keys return false above, so in-grid navigation is untouched.
  const moved = gridRoot ? moveFocusOutsideGrid(gridRoot, !!event.shiftKey) : false;
  if (moved && typeof event.preventDefault === "function") {
    event.preventDefault();
  }
  return true;
}

/**
 * Activate the single interactive control inside the currently focused grid
 * cell / header cell under the exit-model keyboard pattern (WAI-ARIA "grid"
 * pattern, WCAG 2.1.1 Keyboard).
 *
 * With `suppressGridTabToExit` wired to both `suppressKeyboardEvent` and
 * `suppressHeaderKeyboardEvent`, Tab only enters / exits the grid and the Arrow
 * keys move the roving focus across cells and header cells. AG Grid puts focus
 * on the CELL / HEADER-CELL wrapper (not on a child control) and does not
 * forward Enter / Space to a custom control, so an in-cell control (e.g. the
 * row Delete button, the row link, or the per-week "Week controls" header
 * trigger) can be reached with the Arrow keys but not operated with the
 * keyboard. This bridges that gap.
 *
 * A capture-phase keydown listener on the grid host detects Enter / Space while
 * focus is on a cell / header-cell wrapper that holds EXACTLY ONE interactive
 * control (and no cell editor is open); it activates that control and stops AG
 * Grid / the browser from also acting on the key. Cells with NO control
 * (plain / editable cells — Enter must start the editor) and composite
 * containers with MORE THAN ONE control (e.g. a full-width group row that runs
 * its own in-row Arrow-key roving) are deliberately left untouched.
 *
 * `options` scopes which wrappers are handled so activation never fights a
 * grid's own behaviour:
 *   - `bodyCells`  (default true)  — handle `.ag-cell` wrappers. Turn OFF for
 *      grids with editable body cells whose renderer also draws a control
 *      (e.g. a validation-error icon button), so Enter still starts the editor.
 *   - `headerCells` (default true) — handle `.ag-header-cell` wrappers. Turn
 *      OFF when the header cells are plain sortable headers, so Enter keeps
 *      triggering AG Grid's native sort.
 *
 * Returns a handle whose `disconnect()` removes the listener; call it from the
 * component's `ngOnDestroy`.
 */
export function enableGridControlActivation(
  hostEl: HTMLElement | null | undefined,
  options: { bodyCells?: boolean; headerCells?: boolean } = {}
): { disconnect(): void } {
  const handleBody = options.bodyCells ?? true;
  const handleHeader = options.headerCells ?? true;
  if (!hostEl) return { disconnect() {} };
  const doc = hostEl.ownerDocument || document;

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    if (key !== "Enter" && key !== " " && key !== "Spacebar") return;
    // A cell editor owns its own keys (Enter commits / Space types).
    if (hostEl.querySelector(".ag-cell-inline-editing")) return;

    const active = doc.activeElement as HTMLElement | null;
    if (!active || !hostEl.contains(active)) return;
    // Never act when focus is already ON a real control — its native
    // Enter / Space handling must run untouched.
    if (active.matches?.(GRID_INTERACTIVE_CHILD_SELECTOR)) return;

    const isBodyCell = handleBody && active.classList.contains("ag-cell");
    const isHeaderCell = handleHeader && active.classList.contains("ag-header-cell");
    if (!isBodyCell && !isHeaderCell) return;

    const controls = Array.from(
      active.querySelectorAll<HTMLElement>(GRID_INTERACTIVE_CHILD_SELECTOR)
    ).filter((el) => isVisibleFocusable(el) && !el.hasAttribute("disabled"));
    // Exactly one control → activate it. Zero (plain / editable cell) or many
    // (composite container with its own roving) → leave to AG Grid / the cell.
    if (controls.length !== 1) return;

    event.preventDefault();
    event.stopPropagation();
    controls[0].click();
  };

  hostEl.addEventListener("keydown", onKeyDown, true);
  return {
    disconnect() {
      hostEl.removeEventListener("keydown", onKeyDown, true);
    },
  };
}

/**
 * Give a multi-control FULL-WIDTH row (e.g. the "learning curve group" row —
 * expand/collapse toggle, group-name link, "Add Class" button) Up/Down Arrow
 * roving to the adjacent row and Left/Right Arrow roving across its OWN
 * controls (WAI-ARIA "grid" pattern, WCAG 2.1.1 Keyboard, 2.4.3 Focus Order).
 *
 * Full-width rows are NOT governed by `defaultColDef.suppressKeyboardEvent` /
 * `suppressHeaderKeyboardEvent` at all — AG Grid focuses the row's own
 * container element directly (`RowCtrl#onFullWidthRowFocused`) and handles
 * Arrow keys with its OWN row-to-row navigation (`RowCtrl#onKeyboardNavigate`),
 * which never consults the column-level suppressors used everywhere else in
 * the grid, and which ignores Up/Down entirely once focus has moved onto one
 * of the row's own controls.
 *
 * Tab / Shift+Tab are handled separately by `enableGridInteractiveControlTabOrder`
 * (this row's controls are real `tabindex="0"` native Tab stops — see that
 * function's doc comment for why full-width rows need a dedicated, manual Tab
 * handler too).
 *
 * A capture-phase keydown listener on the grid host (same pattern as
 * `enableGridControlActivation`) intercepts key events for any full-width row
 * matching `rowSelector` BEFORE AG Grid's own row-level handlers ever see them:
 *   - Left / Right Arrow move a roving DOM focus across the row's own
 *     interactive controls (`controlSelector`, in DOM order).
 *   - Up / Down Arrow are rerouted through the public `gridApi.setFocusedCell`
 *     so the grid still moves to the previous / next row even when focus is
 *     currently on one of THIS row's own controls (AG Grid's native handler
 *     ignores that case — see above). When focus is already on the row's
 *     container element AG Grid's own Up/Down handling already works and is
 *     left untouched.
 *
 * Returns a handle whose `disconnect()` removes the listener; call it from the
 * component's `ngOnDestroy`.
 */
export function enableFullWidthRowKeyboardNav(
  hostEl: HTMLElement | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi: any,
  rowSelector: string,
  controlSelector: string
): { disconnect(): void } {
  if (!hostEl) return { disconnect() {} };

  const getControls = (row: HTMLElement) =>
    Array.from(row.querySelectorAll<HTMLElement>(controlSelector)).filter(
      (el) => isVisibleFocusable(el) && !el.hasAttribute("disabled")
    );

  const focusAdjacentRow = (delta: number) => {
    const focused = gridApi?.getFocusedCell?.();
    const columns = gridApi?.getAllDisplayedColumns?.();
    const column = columns && columns.length ? columns[0] : undefined;
    if (!focused || !column) return;
    gridApi.setFocusedCell(focused.rowIndex + delta, column);
    gridApi.ensureIndexVisible?.(focused.rowIndex + delta);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    const row = target.closest(rowSelector) as HTMLElement | null;
    if (!row) return;

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      // The row's container element already gets AG Grid's native row-to-row
      // navigation. Only reroute when focus is on one of the row's OWN
      // controls, which AG Grid's handler ignores entirely.
      if (target === row) return;
      event.preventDefault();
      event.stopPropagation();
      focusAdjacentRow(event.key === "ArrowUp" ? -1 : 1);
      return;
    }

    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const controls = getControls(row);
    if (!controls.length) return;

    const currentIndex = controls.indexOf(target);
    let nextIndex: number;
    if (currentIndex === -1) {
      // Focus is on the row container itself → Right enters at the first
      // control, Left enters at the last.
      nextIndex = event.key === "ArrowRight" ? 0 : controls.length - 1;
    } else {
      nextIndex = currentIndex + (event.key === "ArrowRight" ? 1 : -1);
      // At the boundary — leave it to AG Grid (which does nothing here, so
      // focus simply stays put; no wrap-around).
      if (nextIndex < 0 || nextIndex >= controls.length) return;
    }

    event.preventDefault();
    event.stopPropagation();
    controls[nextIndex].focus();
  };

  hostEl.addEventListener("keydown", onKeyDown, true);
  return {
    disconnect() {
      hostEl.removeEventListener("keydown", onKeyDown, true);
    },
  };
}

/**
 * Make Tab / Shift+Tab visit every INTERACTIVE control inside a grid, in true
 * DOCUMENT order, and SKIP every plain read-only cell/header — symmetrically
 * in both directions (WCAG 2.1.1 Keyboard, 2.4.3 Focus Order). Arrow keys are
 * left completely untouched.
 *
 * AG Grid manages its OWN internal Tab handling below the column-level
 * `suppressKeyboardEvent` / `suppressHeaderKeyboardEvent` suppressors, and in
 * two situations that handling wins even when a suppressor returns `true`:
 *   - Full-width rows (e.g. the New Hire "learning curve group" row) are never
 *     routed through the column-level suppressors at all — AG Grid focuses the
 *     row's own container element directly and its row-level Tab handler never
 *     descends into the row's own nested buttons (ESP-18550: "Week controls" /
 *     "Add Class" not reachable with Tab).
 *   - A custom HEADER component's nested control (e.g. the Add Learning Curve
 *     dialog's per-week "Week controls" trigger button) is reachable moving
 *     forward but is SKIPPED moving backward: AG Grid's header cell applies its
 *     own programmatic cell-to-cell Tab navigation only in the reverse
 *     direction, which never lands on the nested child (ESP-18550: "focus order
 *     is not consistent ... skipping the header controls in backward
 *     navigation").
 *
 * This listener is attached in the CAPTURE phase on an ancestor of the grid so
 * it runs, and can `stopPropagation()`, BEFORE AG Grid's own handlers see the
 * key press. On every Tab / Shift+Tab whose target is inside the grid
 * (`.ag-root-wrapper`) it manually finds the next (or, for Shift+Tab, previous)
 * VISIBLE focusable element in true DOM order across the nearest dialog/modal
 * (or the document body) — which may be another interactive control elsewhere
 * in the SAME grid (a different row's own control, a different header's
 * control, this row's own next/previous control) or the first / last focusable
 * element outside the grid entirely (exit) — and focuses it directly,
 * identically for both directions. Plain cells/headers carry no focusable
 * child so they are never selected and are simply skipped, matching the
 * Arrow-key-only navigation used for them elsewhere in the grid. When no
 * destination is found native handling is left untouched so focus never gets
 * trapped. While a cell editor is open (`.ag-cell-inline-editing`) Tab is left
 * to AG Grid so the editor commits and advances to the next cell.
 *
 * A candidate that is an ANCESTOR or DESCENDANT of the current target (e.g.
 * AG Grid's own `.ag-cell` wrapper — which gets a real `tabindex="0"` and its
 * own nested control, such as the New Hire "Management Unit" row link) is
 * skipped: it occupies the exact same on-screen box as the target, so
 * "moving" to it would be visually indistinguishable from doing nothing,
 * reported as focus getting stuck on that cell after a mouse click followed
 * by Tab (ESP-18550).
 *
 * Because this listener `stopPropagation()`s the keydown, AG Grid's OWN
 * internal "currently focused cell" bookkeeping (`FocusService`) never learns
 * that focus moved — AG keeps rendering its own `.ag-cell-focus` ring on the
 * cell it last knew about, WHILE a second, real native focus ring now also
 * appears on the destination: two rings visible at once (ESP-18550). Pass the
 * grid's `gridApi` so this listener can call `setFocusedCell` /
 * `clearFocusedCell` to keep AG's model in sync whenever the destination (or
 * the origin) is an addressable grid body cell.
 *
 * Returns a handle whose `disconnect()` removes the listener; call it from the
 * component's `ngOnDestroy`.
 */
export function enableGridInteractiveControlTabOrder(
  hostEl: HTMLElement | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi?: any
): { disconnect(): void } {
  if (!hostEl) return { disconnect() {} };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Tab") return;
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    const gridRoot = target.closest(".ag-root-wrapper") as HTMLElement | null;
    if (!gridRoot) return;
    if (target.closest(".ag-cell-inline-editing")) return;

    const scope =
      (gridRoot.closest('[role="dialog"], .modal-content, .modal') as HTMLElement) ||
      gridRoot.ownerDocument.body;
    if (!scope) return;

    const focusablesDoc = Array.from(
      scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter(
      (el) =>
        // ESP-21813: `canTakeGridFocus`, NOT the looser `isVisibleFocusable`.
        // FOCUSABLE_SELECTOR ends in a catch-all `[tabindex]:not([tabindex^="-"])`
        // clause with no `:not([disabled])` guard, so it re-admits elements the
        // earlier element-specific clauses deliberately excluded. The
        // learning-curve group row's expand/collapse toggle is exactly that: when
        // the curve has no classes the renderer makes it `disabled` +
        // `visibility: hidden`, but it carries a hardcoded `tabindex="0"`, so it
        // matched. `isVisibleFocusable` then passed it too, because a
        // `visibility: hidden` element still has a layout box and client rects
        // (measured: offsetWidth 24, 1 rect). The walk picked it as the
        // destination, cancelled the native Tab, and called `.focus()` on it —
        // which a disabled, invisible element ignores. Focus therefore stayed on
        // the Ending Date header with the Tab already swallowed: Tab did nothing
        // at all, and the curve title and "Add Class" beyond it were unreachable
        // (WCAG 2.1.1 Keyboard; a cancelled Tab that moves nothing is also 2.1.2
        // No Keyboard Trap). With a class present the toggle is enabled and
        // visible, so it legitimately takes focus and the ring proceeds — which is
        // why the defect only showed for an empty curve.
        canTakeGridFocus(el) &&
        // AG Grid gives the CELL / HEADER-CELL wrapper element ITSELF a
        // `tabindex="0"` as part of its own roving-tabindex model — exactly
        // ONE cell at a time (the last cell reached by an Arrow key or a mouse
        // click). That wrapper `div` then also matches FOCUSABLE_SELECTOR
        // (`[tabindex]:not([tabindex^="-"])`), so Tab / Shift+Tab would treat a
        // plain read-only data cell as a genuine stop. Concretely, Shift+Tab
        // from a control BELOW the grid (e.g. the dialog's Cancel button)
        // jumped straight onto the last roving body cell — "the last edit
        // field" — instead of the header's Week controls, and AG Grid's own
        // focus model then painted a second ring on it (ESP-18550: inconsistent
        // Tab order + dual focus indicator in the Add Learning Curve dialog).
        // Plain data cells are Arrow-key territory only, so exclude the wrapper
        // element here; its genuine focusable CHILDREN (header trigger buttons,
        // in-cell links/buttons) do NOT carry these classes, so they still
        // qualify and remain the real Tab stops.
        !el.classList.contains("ag-cell") &&
        !el.classList.contains("ag-header-cell") &&
        // AG Grid also renders two 0×0 `.ag-tab-guard` sentinel <div>s (one
        // before, one after the grid body) and toggles them to `tabindex="0"`
        // whenever the grid is part of the page tab flow, so they too match
        // FOCUSABLE_SELECTOR. Focusing a tab guard fires AG's own focus handler,
        // which immediately redirects focus onto the grid's last-focused CELL —
        // exactly the data-cell landing we are trying to prevent — so they must
        // never be treated as Tab stops (ESP-18550).
        !el.classList.contains("ag-tab-guard")
    );

    // Order the Tab stops by their VISUAL position (top-to-bottom, then
    // left-to-right) rather than DOM document order. AG Grid renders pinned
    // columns, the horizontally-scrollable centre columns and full-width rows
    // (group separators, the "Add Class" row) in SEPARATE DOM containers, so
    // document order does NOT match what the user sees: Tab would zig-zag
    // across the grid and appear to skip the week-header controls under
    // "Management" and the "Add Class" buttons (ESP-18550: "focus order is
    // not logical"). Group every control by its owning grid row so all cells
    // on one visual row share a single vertical band regardless of which
    // container renders them, then sort bands top-to-bottom and controls
    // within a band left-to-right. Array.sort is stable and we tiebreak on
    // the original document index, so controls that resolve to the same
    // position keep their existing document order.
    // Read each element's rect only ONCE and cache each owning row's top so a
    // grid with many interactive controls does not force repeated layout on
    // every Tab keydown (controls that share a row reuse the cached band top).
    const bandTopCache = new Map<HTMLElement, number>();
    const bandTop = (el: HTMLElement, ownTop: number): number => {
      const row = el.closest(
        ".ag-row, .ag-header-row, .ag-full-width-row"
      ) as HTMLElement | null;
      if (!row) return ownTop;
      let top = bandTopCache.get(row);
      if (top === undefined) {
        top = row.getBoundingClientRect().top;
        bandTopCache.set(row, top);
      }
      return top;
    };
    const focusables = focusablesDoc
      .map((el, i) => {
        const rect = el.getBoundingClientRect();
        return { el, i, top: bandTop(el, rect.top), left: rect.left };
      })
      .sort((a, b) => a.top - b.top || a.left - b.left || a.i - b.i)
      .map((entry) => entry.el);

    // AG Grid itself puts real DOM focus on the CELL / HEADER-CELL wrapper
    // (giving it tabindex="0" as part of its own roving-tabindex bookkeeping)
    // rather than on a nested control, e.g. when a cell is entered by mouse
    // click instead of Tab. That wrapper then matches FOCUSABLE_SELECTOR
    // (`[tabindex]:not([tabindex^="-"])`) too, and — because it is an
    // ANCESTOR of its own nested control — sits immediately next to it in
    // document order. Without this guard, the very first Tab press "moves"
    // focus from the wrapper onto its own child control, which occupies the
    // exact same on-screen box: nothing visibly changes and the grid appears
    // stuck (ESP-18550). Skip any candidate that is an ancestor or descendant
    // of the current target — same visual cell, not a real next/previous
    // stop. This ONLY applies when `target` itself is a genuine stop (found
    // in `focusables`, below): a non-focusable container reached via the
    // compareDocumentPosition fallback (e.g. a full-width row wrapper with
    // tabindex="-1") is NOT itself a stop, so Tab entering its first nested
    // control there is the intended, existing behavior.
    const isSameStop = (el: HTMLElement) =>
      el === target || el.contains(target) || target.contains(el);

    const backwards = !!event.shiftKey;
    const idx = focusables.indexOf(target);
    // AG Grid's roving cell / header-cell WRAPPER is no longer itself a Tab
    // stop (filtered out of `focusablesDoc` above), so a click / Arrow-focused
    // wrapper reaches the fallback branch below with `idx === -1`. When it
    // does, never "advance" into its OWN nested child: the child occupies the
    // exact same on-screen box, so focus would look stuck (ESP-18550). Its
    // genuine sibling controls elsewhere in the grid remain valid destinations.
    const targetIsRovingCell =
      target.classList.contains("ag-cell") ||
      target.classList.contains("ag-header-cell");
    let dest: HTMLElement | null = null;
    if (idx !== -1) {
      const step = backwards ? -1 : 1;
      for (let i = idx + step; i >= 0 && i < focusables.length; i += step) {
        if (!isSameStop(focusables[i])) {
          dest = focusables[i];
          break;
        }
      }
    } else {
      for (const el of focusablesDoc) {
        if (targetIsRovingCell && target.contains(el)) continue;
        const pos = target.compareDocumentPosition(el);
        if (!backwards) {
          if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
            dest = el;
            break;
          }
        } else if (pos & Node.DOCUMENT_POSITION_PRECEDING) {
          dest = el;
        } else if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
          break;
        }
      }
    }

    if (!dest) return;

    // a11y (WCAG 2.1.1 Keyboard / ESP-21804): `dest` may belong to a column
    // that is currently scrolled out of view (e.g. a later week's "Week
    // controls" header trigger while the grid is scrolled left). Focusing it
    // directly would let the BROWSER perform its own scroll-into-view, which
    // only moves the ONE ancestor viewport containing `dest`
    // (`.ag-header-viewport` for a header control, `.ag-center-cols-viewport`
    // for a body cell) — it has no idea the OTHER viewport exists, so the
    // header and body visibly drift apart. This is why dragging AG Grid's own
    // scrollbar (which runs AG Grid's internal scroll-sync) keeps the header
    // aligned with the grid, but tabbing onto an off-screen header control did
    // not (ESP-21804). Ask AG Grid's own API to bring the column into view
    // FIRST — `ensureColumnVisible` drives the same internal path a scrollbar
    // drag uses, which mirrors the resulting scroll position to BOTH
    // viewports — then focus with `preventScroll: true` so the browser does
    // not ALSO perform its own uncoordinated scroll on top of that.
    const destCell = dest.closest("[col-id]") as HTMLElement | null;
    const colId = destCell?.getAttribute("col-id");
    if (colId && gridApi && typeof gridApi.ensureColumnVisible === "function") {
      gridApi.ensureColumnVisible(colId);
    }
    dest.focus({ preventScroll: true });

    // ESP-21813 anti-trap (WCAG 2.1.2 No Keyboard Trap): only take ownership of
    // the Tab once focus has ACTUALLY moved. Swallowing the key and then failing
    // to move focus leaves the user stuck with no way forward, which is strictly
    // worse than an imperfect Tab order. `canTakeGridFocus` above should keep an
    // unfocusable element out of the candidate list in the first place; this is
    // the backstop for anything it cannot predict (a control made inert or
    // removed between the walk and the focus call), and it costs nothing when the
    // focus succeeded.
    const doc = gridRoot.ownerDocument;
    const landed = doc.activeElement === dest || dest.contains(doc.activeElement);
    if (!landed) return;

    event.preventDefault();
    event.stopPropagation();

    // ESP-21813: a FULL-WIDTH-ROW destination needs its focus re-asserted. AG
    // Grid's `RowCtrl.onFullWidthRowFocused` calls `focus()` on the ROW
    // CONTAINER whenever its row index matches the grid's focused cell, and it
    // does so on a LATER TICK — stealing DOM focus off the control we just
    // focused and parking it on a `tabindex="-1"` row div that paints no ring.
    // To the user, Tab appeared to do nothing at all.
    //
    // The `setFocusedCell` branch below already re-asserts focus for an
    // addressable cell after AG Grid steals it the same way; this is the same
    // problem on the full-width side. It is deliberately OUTSIDE that branch:
    // this is about DOM focus, not about AG Grid's focused-cell model, so it
    // must not depend on which model methods the api happens to expose. It is a
    // no-op for any destination that is not inside a full-width row.
    //
    // Most visible when the full-width group row is the ONLY thing in the body —
    // a learning curve with no classes — because with class rows present Tab's
    // first destination is a cell-hosted control, which takes the branch below
    // and hides the bug (exactly the "works when the curve has a class" report).
    reassertFocusOnFullWidthRowControl(dest);

    if (gridApi && typeof gridApi.setFocusedCell === "function") {
      const destRow = dest.closest("[row-index]") as HTMLElement | null;
      const rowIndex = destRow ? Number(destRow.getAttribute("row-index")) : NaN;
      if (colId && !Number.isNaN(rowIndex)) {
        // Destination is an addressable grid body cell — tell AG Grid's own
        // model it is now current so it repaints its ring there and drops
        // the stale one on wherever it last thought focus was. AG Grid's
        // `setFocusedCell` ALWAYS calls the cell's own `focusCell` with
        // `forceBrowserFocus: true` (see FocusService.setFocusedCell /
        // CellCtrl.onCellFocused in ag-grid-community), which synchronously
        // moves REAL DOM focus onto the `.ag-cell` WRAPPER element itself —
        // stealing it back from the nested control we just focused (`dest`),
        // e.g. the Delete button. That leaves DOM focus sitting on the
        // wrapper (same visual box as the button, so the ring looks like it
        // never left) instead of the actual control (ESP-18550: "focus stuck
        // on Eastern America" — it had actually moved to the Delete cell's
        // wrapper, not the button). Re-focus `dest` again afterwards so real
        // DOM focus ends up back on the intended control while AG's model
        // still reflects the correct row/column.
        gridApi.setFocusedCell(rowIndex, colId);
        dest.focus({ preventScroll: true });
      } else if (typeof gridApi.clearFocusedCell === "function") {
        // Destination is NOT an addressable body cell: either Tab left the
        // grid entirely, or it landed on a HEADER control (e.g. a week-day
        // header trigger under "Management") or a FULL-WIDTH-ROW control
        // (e.g. an "Add Class" button) — neither of which AG Grid's
        // focused-cell model can address. Clear that model so AG stops
        // painting its cell / roving-header ring on whichever cell it last
        // thought was focused; otherwise that stale ring lingers ALONGSIDE
        // the real browser focus ring on `dest` (ESP-18550: "dual focus
        // indicator is visible sometimes on the header").
        gridApi.clearFocusedCell();
      }
    }
  };

  hostEl.addEventListener("keydown", onKeyDown, true);

  // --- Grid ENTRY from an adjacent control (dialog / modal only) ---
  // The per-grid listener above fires only for keydowns whose target is a
  // DESCENDANT of the grid host, so it handles Tab WITHIN the grid and Tab
  // OUT of it, but it can never see the keydown that Tabs INTO the grid from a
  // control sitting just before/after it (that keydown fires on the outside
  // control). Left to the browser, native Tab first lands on one of AG Grid's
  // tab-guard sentinels, whose focus handler then drops focus onto the grid's
  // last-focused (or first) CELL — a plain, non-interactive data cell. In the
  // Add Learning Curve dialog that meant Shift+Tab from "Cancel" landed on the
  // last week's edit cell and Tab from the weeks field landed on the
  // "Parameter" header cell, instead of the week header controls, and AG's own
  // focus model painted a second ring there (ESP-18550: "Tab ... lands
  // directly on the last edit field" + "dual focus indicator is visible").
  // Intercept entry at the dialog/modal scope: if the curated next/previous
  // focusable (same visual-order rules, cell/header-cell wrappers excluded) is
  // inside THIS grid, focus it directly so entry lands on the first/last
  // INTERACTIVE control, never on a data cell. This is scoped to dialogs/modals
  // so page-level grids (whose entry already lands on genuine in-cell controls)
  // are left untouched.
  const entryScope = hostEl.closest(
    '[role="dialog"], .modal-content, .modal'
  ) as HTMLElement | null;
  const onEntryKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Tab" || !entryScope) return;
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    const gridRoot = hostEl.querySelector(
      ".ag-root-wrapper"
    ) as HTMLElement | null;
    if (!gridRoot) return;
    // Only handle ENTRY: focus currently OUTSIDE this grid. Tab within / out of
    // the grid is already handled by the per-grid listener above.
    if (gridRoot.contains(target)) return;
    if (target.closest(".ag-cell-inline-editing")) return;

    const bandTopCache = new Map<HTMLElement, number>();
    const focusables = Array.from(
      entryScope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    )
      .filter(
        (el) =>
          isVisibleFocusable(el) &&
          !el.classList.contains("ag-cell") &&
          !el.classList.contains("ag-header-cell") &&
          // Skip AG Grid's `.ag-tab-guard` sentinels (see the per-grid filter
          // above): they are `tabindex="0"` while the grid is in the tab flow
          // but focusing one bounces focus into a body cell (ESP-18550).
          !el.classList.contains("ag-tab-guard")
      )
      .map((el, i) => {
        const rect = el.getBoundingClientRect();
        const row = el.closest(
          ".ag-row, .ag-header-row, .ag-full-width-row"
        ) as HTMLElement | null;
        let top = rect.top;
        if (row) {
          let cached = bandTopCache.get(row);
          if (cached === undefined) {
            cached = row.getBoundingClientRect().top;
            bandTopCache.set(row, cached);
          }
          top = cached;
        }
        return { el, i, top, left: rect.left };
      })
      .sort((a, b) => a.top - b.top || a.left - b.left || a.i - b.i)
      .map((entry) => entry.el);

    const idx = focusables.indexOf(target);
    if (idx === -1) return;
    const dest = focusables[idx + (event.shiftKey ? -1 : 1)];
    // Only intervene when the next stop actually enters THIS grid; otherwise
    // leave native Tab alone (movement between two outside controls).
    if (!dest || !gridRoot.contains(dest)) return;

    event.preventDefault();
    event.stopPropagation();
    dest.focus();
    // Entry lands on a header / interactive control, never an addressable body
    // cell, so drop any stale focused-cell model to avoid AG painting a second
    // ring (ESP-18550 dual focus).
    if (gridApi && typeof gridApi.clearFocusedCell === "function") {
      gridApi.clearFocusedCell();
    }
  };
  if (entryScope) {
    entryScope.addEventListener("keydown", onEntryKeyDown, true);
  }

  return {
    disconnect() {
      hostEl.removeEventListener("keydown", onKeyDown, true);
      if (entryScope) {
        entryScope.removeEventListener("keydown", onEntryKeyDown, true);
      }
    },
  };
}

/**
 * Let the Down Arrow move focus from an interactive control inside a HEADER
 * cell into the first body cell of that column (WAI-ARIA "grid" pattern,
 * WCAG 2.1.1 Keyboard, 2.4.3 Focus Order).
 *
 * Under the exit-model keyboard pattern (`suppressGridTabToExit` +
 * `enableGridInteractiveControlTabOrder`) Tab ENTERS the grid onto a header
 * INTERACTIVE CONTROL — e.g. the per-week "Week controls" trigger button —
 * rather than onto the header-cell wrapper. AG Grid's own Arrow-key cell
 * navigation only runs when the `.ag-header-cell` / `.ag-cell` WRAPPER element
 * holds real DOM focus; with focus sitting on the nested control the ArrowDown
 * keydown fires on the button and AG Grid never sees it, so the editable body
 * cells below the header stay unreachable and keyboard focus is stuck on the
 * header (ESP-18552: the AHT / % Productive edit cells in the Add Learning
 * Curve dialog could not be reached with the keyboard).
 *
 * A capture-phase keydown listener on the grid host detects ArrowDown while
 * focus is on an interactive control INSIDE a header cell and moves the grid's
 * focused cell to row 0 of that same column via the public `setFocusedCell`,
 * which puts real DOM focus on the body cell. From there AG Grid's native
 * Arrow navigation reaches every remaining cell, and ArrowUp returns to the
 * header cell as usual. Focus already on the header-cell WRAPPER itself is
 * left untouched (AG Grid handles that natively), and the listener no-ops when
 * the column is not an addressable body column or the grid has no rows.
 *
 * Returns a handle whose `disconnect()` removes the listener; call it from the
 * component's `ngOnDestroy`.
 */
export function enableGridHeaderControlArrowNav(
  hostEl: HTMLElement | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi?: any
): { disconnect(): void } {
  if (!hostEl || !gridApi || typeof gridApi.setFocusedCell !== "function") {
    return { disconnect() {} };
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "ArrowDown") return;
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    // Only act when focus is on a genuine interactive control that lives INSIDE
    // a header cell. Focus on the header-cell wrapper itself is AG Grid's own
    // Arrow-key territory and must be left untouched.
    const headerCell = target.closest(".ag-header-cell") as HTMLElement | null;
    if (!headerCell || target === headerCell) return;
    if (!target.matches?.(GRID_INTERACTIVE_CHILD_SELECTOR)) return;
    const colId = headerCell.getAttribute("col-id");
    if (!colId) return;
    // Need at least one body row to descend into.
    const rowCount =
      typeof gridApi.getDisplayedRowCount === "function"
        ? gridApi.getDisplayedRowCount()
        : 0;
    if (!rowCount) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof gridApi.ensureIndexVisible === "function") {
      gridApi.ensureIndexVisible(0);
    }
    // Moves REAL DOM focus onto the body cell wrapper (forceBrowserFocus), so
    // the user lands on the first editable cell of the column and AG Grid's
    // native Arrow navigation takes over from there.
    gridApi.setFocusedCell(0, colId);
  };

  hostEl.addEventListener("keydown", onKeyDown, true);
  return {
    disconnect() {
      hostEl.removeEventListener("keydown", onKeyDown, true);
    },
  };
}

/**
 * Keep the keyboard-focused grid column clear of a CSS-`sticky` first column
 * (WCAG 2.4.11 Focus Not Obscured (Minimum), 2.1.1 Keyboard, 2.4.3 Focus Order).
 *
 * Some grids "freeze" their leading row-header column with plain CSS —
 * `position: sticky; left: 0` on that column's header and body cells — instead
 * of AG Grid's own `pinned: 'left'`. The Add Learning Curve dialog does exactly
 * that for its "Parameter" column, deliberately: pinning made AG Grid render
 * every logical row twice (pinned-left + centre) with the same
 * `aria-rowindex`, so JAWS double-counted rows (ESP-18571 / ESP-18588).
 *
 * The cost of the CSS freeze is that AG Grid does not know the column is
 * frozen. Its scroll maths (`GridBodyScrollFeature.isColumnOutsideViewport` /
 * `getPositionedHorizontalScroll`) treats the whole centre viewport as
 * visible, so:
 *
 * 1. A column is judged "already visible" as soon as its left edge is at or
 *    after the viewport's scroll offset — even when the opaque sticky column is
 *    painted on top of it. Arrowing LEFT from week 2 to week 1 therefore
 *    scrolled nothing at all and week 1 stayed hidden underneath "Parameter",
 *    only appearing once focus reached the Parameter header itself (which
 *    scrolls to 0). (ESP-21807)
 * 2. When it does scroll a column in from the left it aligns that column's
 *    left edge to the viewport's left edge — i.e. straight under the sticky
 *    column.
 *
 * On top of that, AG Grid only runs its own scroll maths on the code paths it
 * owns (Arrow-key header/cell navigation). Tab moves focus onto a header cell
 * or onto an interactive control INSIDE one through the browser's / its
 * `ManagedFocusFeature`'s focus calls, which do not go through
 * `ensureColumnVisible`, so tabbing towards a week that was scrolled out of
 * view did not scroll at all — the reported "arrow keys scroll, Tab does not".
 * (ESP-21807)
 *
 * This listener fixes both by correcting the scroll position from the FOCUS
 * event rather than from any one key handler, so every route into a column
 * (Tab, Arrow keys, `setFocusedCell`, a mouse click) is covered. Whenever
 * focus lands inside a `[col-id]` cell it re-runs AG Grid's visibility test
 * with the sticky column's width treated as dead space on the left, and
 * scrolls only when the focused column is actually obscured:
 *
 *   colLeft  < scrollLeft + stickyWidth  ->  scrollLeft = colLeft - stickyWidth
 *   colRight > scrollLeft + viewportWidth ->  scrollLeft = colRight - viewportWidth
 *
 * The scroll is applied by writing `scrollLeft` on `.ag-center-cols-viewport`,
 * which is precisely what AG Grid's own `ensureColumnVisible` does
 * (`RowContainerCtrl.setCenterViewportScrollLeft`), so AG Grid's scroll
 * listener mirrors the new position to the header and the fake scrollbar. The
 * header viewport is then re-asserted explicitly: a `focus()` call that AG
 * Grid makes without `preventScroll` lets the BROWSER scroll-into-view the one
 * ancestor viewport that contains the focused element (`.ag-header-viewport`
 * for a header control), which is what made the header visibly drift out of
 * alignment with the body (ESP-21804). Re-pointing it at the centre viewport's
 * offset keeps the two in lockstep even when our own scroll position did not
 * have to change.
 *
 * `stickyColId` must be the `colId` of the CSS-frozen column, and the freeze
 * must be at the START of the scroll area. The maths is left-to-right only —
 * matching the `left: 0` the CSS pins the column with — so an RTL grid needs
 * the mirrored branches AG Grid keeps behind its own `enableRtl` flag. Focus
 * landing on the frozen column itself is ignored: it is never obscured.
 *
 * Returns a handle whose `disconnect()` removes the listener; call it from the
 * component's `ngOnDestroy`.
 */
export function keepFocusClearOfStickyGridColumn(
  hostEl: HTMLElement | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi: any,
  options: { stickyColId: string }
): { disconnect(): void } {
  const stickyColId = options?.stickyColId;
  if (!hostEl || !stickyColId) return { disconnect() {} };

  const onFocusIn = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    const cell = target.closest("[col-id]") as HTMLElement | null;
    const colId = cell?.getAttribute("col-id");
    // The frozen column is painted on top of the scroll area, so focus landing
    // on it is never obscured and there is nothing to correct. Note this leaves
    // the offset untouched, so week 1 can still be sitting under the frozen
    // column while focus is on it — that is not a Focus Not Obscured concern,
    // and AG Grid's own Arrow navigation scrolls to 0 here anyway.
    if (!colId || colId === stickyColId) return;

    const viewport = hostEl.querySelector(
      ".ag-center-cols-viewport"
    ) as HTMLElement | null;
    if (!viewport) return;
    const viewportWidth = viewport.clientWidth;
    if (!viewportWidth) return;

    const bounds = getGridColumnBounds(hostEl, gridApi, colId);
    if (!bounds) return;
    // How much of the viewport's left edge the frozen column permanently
    // covers: its RIGHT edge, not its width, and read through the same helper
    // as the focused column so both sides of the subtraction below are in the
    // column model's coordinate space (mixing that with a rendered
    // `getBoundingClientRect()` width can leave the column a sliver short).
    const stickyBounds = getGridColumnBounds(hostEl, gridApi, stickyColId);
    const deadSpace = stickyBounds ? stickyBounds.right : 0;

    const scrollLeft = viewport.scrollLeft;
    let next = scrollLeft;
    // Obscured on the left (fully or partly under the frozen column): bring it
    // to the first pixel that the frozen column does NOT cover. Checked first
    // so a column wider than the free space still shows its start.
    if (bounds.left < scrollLeft + deadSpace) {
      next = bounds.left - deadSpace;
    } else if (bounds.right > scrollLeft + viewportWidth) {
      next = bounds.right - viewportWidth;
    }
    // Rounded like AG Grid's own scroll handler does, so a fractional column
    // width cannot leave `next` permanently unequal to the offset the browser
    // stored and make every focus event rewrite the same position.
    next = Math.round(
      Math.min(Math.max(next, 0), Math.max(0, viewport.scrollWidth - viewportWidth))
    );

    if (next !== scrollLeft) {
      viewport.scrollLeft = next;
      // Hand the new offset back to AG Grid so it settles the viewport the way
      // its own scroll path would. Writing `scrollLeft` alone is not enough:
      // AG Grid syncs the viewport in
      // `RowContainerCtrl.onHorizontalViewportChanged`, which on the scroll
      // path is reached through `onHScroll` — and `onHScroll` DISCARDS the
      // event whenever another scroll source (a scrollbar drag within the last
      // 500ms) still holds `GridBodyScrollFeature.isControllingScroll`.
      // `ensureColumnVisible` calls `onHorizontalViewportChanged` and
      // `flushAllFrames` unconditionally, so it runs regardless of that latch.
      // (Re-virtualising the columns that just came into view used to be part
      // of this reason. The only grid that uses this helper now sets
      // `suppressColumnVirtualisation` — it has to, or its CSS-frozen column
      // would be dropped from the DOM, ESP-21807 — so that half no longer
      // applies; the `flushAllFrames` half still does.) The column now sits
      // inside the viewport, so
      // AG Grid's own (frozen-column-unaware) maths asks for no further
      // scroll — except for a column too wide to fit beside the frozen one,
      // where it would align the right edge and re-hide the start, so our
      // offset is re-asserted.
      if (gridApi && typeof gridApi.ensureColumnVisible === "function") {
        gridApi.ensureColumnVisible(colId);
        if (viewport.scrollLeft !== next) {
          viewport.scrollLeft = next;
        }
      }
    }

    // Undo any browser scroll-into-view that moved the header on its own, and
    // cover the case where our own offset did not change (ESP-21804). This is
    // NOT the scroll-event-driven header/body mirror that ESP-21579 removed
    // (see `makeGridScrollRegionFocusable`): it runs on focus rather than on
    // scroll, so it cannot race a scroll in progress, and it writes the exact
    // absolute offset AG Grid's own `setHorizontalScrollPosition` writes here
    // rather than mirroring a delta, so it is idempotent and cannot compound.
    // AG Grid's fake horizontal scrollbar is deliberately left alone — writing
    // it would hand `isControllingScroll` to the fake container and gate out
    // AG Grid's next genuine header sync, which is exactly the fight ESP-21579
    // warns about. AG Grid re-seats the thumb on the next real scroll.
    const headerViewport = hostEl.querySelector(
      ".ag-header-viewport"
    ) as HTMLElement | null;
    if (headerViewport && headerViewport.scrollLeft !== viewport.scrollLeft) {
      headerViewport.scrollLeft = viewport.scrollLeft;
    }
  };

  hostEl.addEventListener("focusin", onFocusIn, true);
  return {
    disconnect() {
      hostEl.removeEventListener("focusin", onFocusIn, true);
    },
  };
}

/**
 * Left / right edges of a column in the CENTRE container's content coordinate
 * space — the same space as `.ag-center-cols-viewport`'s `scrollLeft`, which is
 * what AG Grid's own `GridBodyScrollFeature.getColumnBounds` uses. Prefers the
 * column model (authoritative, and correct for columns virtualised out of the
 * DOM) and falls back to the rendered cell's offsets.
 */
function getGridColumnBounds(
  hostEl: HTMLElement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi: any,
  colId: string
): { left: number; right: number } | null {
  const column = gridApi?.getColumn?.(colId);
  if (column) {
    const left = column.getLeft?.();
    const width = column.getActualWidth?.();
    if (typeof left === "number" && typeof width === "number") {
      return { left, right: left + width };
    }
  }
  const cell = hostEl.querySelector(
    `.ag-header-cell[col-id="${colId}"], .ag-cell[col-id="${colId}"]`
  ) as HTMLElement | null;
  if (!cell) return null;
  return { left: cell.offsetLeft, right: cell.offsetLeft + cell.offsetWidth };
}

/**
 * Start a grid's horizontal scrollbar AFTER a CSS-frozen ("fake pinned") column,
 * so the frozen column is excluded from horizontal scrolling and the scrollbar
 * is neither visible nor operable within it (ESP-22062).
 *
 * A column frozen with `position: sticky` rather than AG Grid's `pinned: 'left'`
 * still lives INSIDE the centre container, so AG Grid renders no left spacer and
 * its horizontal scrollbar spans the whole grid — the thumb travels underneath
 * the opaque frozen column. (With real pinning AG Grid sizes
 * `.ag-horizontal-left-spacer` to `getDisplayedColumnsLeftWidth()`, which is what
 * produces the expected seam.) This reproduces that geometry for a sticky column.
 *
 * TWO writes are required, and the second one is the whole point:
 *   1. grow `.ag-horizontal-left-spacer` to the frozen column's width `S`, and
 *   2. shrink `.ag-body-horizontal-scroll-container` by the same `S`.
 *
 * Growing the spacer ALONE is worse than the bug it fixes. The fake viewport's
 * client width drops by `S` while its content stays, so its scroll range exceeds
 * the centre viewport's by `S`. AG Grid's `onFakeHScroll` passes the fake
 * viewport's raw `scrollLeft` to `doHorizontalScroll` (its
 * `shouldBlockScrollUpdate` guard is touch-only), which sets the header
 * container's transform from that raw value while the browser CLAMPS the centre
 * viewport — so the last `S` pixels of thumb travel do nothing AND the header
 * drifts out of alignment with the body at the right-hand end, with
 * `GridBodyScrollFeature`'s cached `scrollLeft` left wrong (which then feeds
 * `ensureColumnVisible`, i.e. ESP-21804 and ESP-21807 territory). Reducing both
 * sides keeps the ranges identical:
 *
 *     fakeMax = (content − S) − (client − S) = content − client = centreMax
 *
 * `S` is read from the column model, NOT hardcoded: the frozen column inherits
 * `defaultColDef.resizable`, so the user can drag it wider.
 *
 * This writes ONLY widths — never `scrollLeft` — and never listens for `scroll`,
 * so it cannot reintroduce the hand-rolled scroll mirroring that ESP-21579
 * removed. It also has no accessibility surface at all: AG Grid renders the
 * scrollbar row as `aria-hidden="true"`.
 *
 * Re-application is driven primarily by a `MutationObserver`, because AG Grid
 * rewrites both widths from INTERNAL events that `api.addEventListener` cannot
 * reach (`setFakeHScrollSpacerWidths` on `displayedColumnsWidthChanged` /
 * `scrollVisibilityChanged`, and `CenterWidthFeature` on
 * `columnContainerWidthChanged`). The public grid events are added as a
 * same-frame fast path. Every write is guarded on the value actually differing,
 * so our own writes do not re-trigger the observer indefinitely.
 *
 * Note this needs `.ag-horizontal-left-spacer { overflow: hidden }` (set globally
 * in `grid.scss` for the balham theme, overriding AG Grid's own
 * `overflow-x: scroll`) — that is what stops the now full-width spacer rendering
 * a scrollbar of its own inside the frozen area.
 *
 * Returns a handle whose `disconnect()` detaches everything; call it from the
 * component's `ngOnDestroy`. Pass the component host element from `onGridReady`.
 */
export function confineGridHScrollToStickyColumn(
  hostEl: HTMLElement | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridApi: any,
  options: { stickyColId: string }
): { disconnect(): void } {
  const stickyColId = options?.stickyColId;
  if (!hostEl || !stickyColId) return { disconnect() {} };

  const scrollRow = hostEl.querySelector(
    ".ag-body-horizontal-scroll"
  ) as HTMLElement | null;
  if (!scrollRow) return { disconnect() {} };

  const apply = () => {
    const spacer = scrollRow.querySelector(
      ".ag-horizontal-left-spacer"
    ) as HTMLElement | null;
    const container = scrollRow.querySelector(
      ".ag-body-horizontal-scroll-container"
    ) as HTMLElement | null;
    const centreViewport = hostEl.querySelector(
      ".ag-center-cols-viewport"
    ) as HTMLElement | null;
    if (!spacer || !container || !centreViewport) return;

    // The frozen column's RIGHT edge, in the centre container's coordinate
    // space — the same space as the viewport's scrollLeft. For a sticky column
    // at left 0 that is its width, but reading the edge keeps this correct if it
    // ever stops being the first column.
    const bounds = getGridColumnBounds(hostEl, gridApi, stickyColId);
    const sticky = bounds ? Math.round(bounds.right) : 0;
    if (sticky <= 0) return;

    // The true scrollable content the thumb has to represent. Taken from the
    // rendered viewport rather than re-derived from the column model so it
    // cannot drift from what the browser is actually scrolling.
    const content = Math.round(centreViewport.scrollWidth);
    if (content <= 0) return;

    const spacerWidth = `${sticky}px`;
    const containerWidth = `${Math.max(0, content - sticky)}px`;

    // Write the same three properties AG Grid's own `setFixedWidth` writes, so
    // ours is simply the later value with no specificity fight.
    if (spacer.style.width !== spacerWidth) spacer.style.width = spacerWidth;
    if (spacer.style.minWidth !== spacerWidth) spacer.style.minWidth = spacerWidth;
    if (spacer.style.maxWidth !== spacerWidth) spacer.style.maxWidth = spacerWidth;
    if (container.style.width !== containerWidth) {
      container.style.width = containerWidth;
    }
  };

  apply();

  const observer = new MutationObserver(apply);
  observer.observe(scrollRow, {
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class"],
  });

  const GRID_EVENTS = [
    "columnResized",
    "displayedColumnsChanged",
    "gridSizeChanged",
  ];
  const hasApi = !!gridApi && typeof gridApi.addEventListener === "function";
  if (hasApi) {
    GRID_EVENTS.forEach((event) => gridApi.addEventListener(event, apply));
  }

  return {
    disconnect() {
      observer.disconnect();
      if (hasApi && typeof gridApi.removeEventListener === "function") {
        GRID_EVENTS.forEach((event) => gridApi.removeEventListener(event, apply));
      }
    },
  };
}

/**
 * Make the Tab key visit ONLY the interactive controls inside a data grid — the
 * row selection checkbox, the row's primary link/button (e.g. Plan Name) and
 * the row action icon (e.g. Delete) — and SKIP every read-only text cell
 * (WCAG 2.1.1 Keyboard, 2.4.3 Focus Order, 3.2.4 Consistent Identification).
 *
 * A grid that mixes interactive controls with plain text cells must NOT make the
 * plain cells Tab stops. Doing so (a) forces keyboard users through redundant
 * stops and (b), when every cell in a row triggers the same row-open action,
 * exposes several differently named controls that all do the same thing — which
 * confuses screen-reader users. The correct pattern is: Tab lands on each
 * interactive control in order (all sortable headers → row checkbox → row link →
 * row action), while the remaining cells are reachable only with the Arrow keys.
 *
 * Returning `true` for Tab tells AG Grid to skip its own cell-to-cell Tab
 * navigation WITHOUT calling preventDefault, so the browser's NATIVE Tab runs.
 * Native Tab moves to the next element whose tabindex is >= 0; because only the
 * interactive controls carry tabindex="0" (AG Grid keeps every other cell
 * wrapper at tabindex="-1"), native Tab lands only on those controls, in DOM
 * order, and skips the plain cells. Non-Tab keys are untouched, so Arrow-key
 * navigation still reaches every cell. While a cell editor is open Tab is left
 * to AG Grid so the editor commits and advances to the next cell.
 *
 * Use as the `suppressKeyboardEvent` callback in `defaultColDef` (compose with
 * `combineGridKeyboardSuppressors` to keep Home/End handling).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function suppressGridTabToInteractiveControls(params: any): boolean {
  if (params?.event?.key !== "Tab") return false;
  // Let the cell editor own Tab (commit + move to next cell).
  return !params.editing;
}

/**
 * Compose multiple `suppressKeyboardEvent` handlers into one.
 *
 * AG Grid calls a single `suppressKeyboardEvent` per cell — a column-level
 * definition overrides `defaultColDef` entirely, so any column with its own
 * handler (e.g. `tabSuppressKeyboardEventForFocusableChildren`) silently loses
 * the Home/End handling supplied by `suppressHomeEndKeyboardEvent` in
 * `defaultColDef`. Wrapping both handlers with this utility restores the
 * correct precedence: the first suppressor that returns `true` wins and the
 * rest are skipped.
 *
 * Usage in column definitions that already specify suppressKeyboardEvent:
 *   suppressKeyboardEvent: combineGridKeyboardSuppressors(
 *     suppressHomeEndKeyboardEvent,
 *     tabSuppressKeyboardEventForFocusableChildren
 *   )
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function combineGridKeyboardSuppressors(
  ...suppressors: Array<(params: any) => boolean>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): (params: any) => boolean {
  return (params: any) => suppressors.some((s) => s(params));
}

/**
 * Interactive controls a grid cell/header may render (drill-down "+MAR2021"
 * button, validation-error icon button, "Copy base to all weeks" / "Reset to
 * base" buttons, etc). Deliberately NARROW — it must NOT match the focused
 * cell/header wrapper itself (which carries a `tabindex`), only genuine
 * controls.
 */
const GRID_INTERACTIVE_CHILD_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"]';

/**
 * Keys that either ACTIVATE a native control (Enter / Space) or would start an
 * AG Grid cell edit (F2). When keyboard focus is ON an interactive child
 * control these keys must be owned by that control ALONE — AG Grid must not
 * ALSO act on them (e.g. start the cell's edit action), otherwise activating
 * the control performs two actions at once.
 */
const GRID_CONTROL_ACTIVATION_KEYS = new Set(["Enter", " ", "Spacebar", "F2"]);

/**
 * Make interactive controls rendered inside custom HEADER components reachable
 * with the Tab key (WCAG 2.2 SC 2.1.1 Keyboard).
 *
 * AG Grid's regular column header cell registers `onTabKeyDown: () => null`, so
 * pressing Tab while a header cell has focus does NOT move focus to a focusable
 * child (e.g. the "+MAR2021" drill-down button, or the "Copy base to all weeks"
 * / "Reset to base" buttons) — AG Grid only navigates cell-to-cell, so those
 * header buttons never land in the Tab order.
 *
 * Returning `true` from `suppressHeaderKeyboardEvent` tells AG Grid to stop its
 * own Tab handling WITHOUT calling preventDefault, so the browser's native Tab
 * runs (the header cell wrapper is `tabindex="-1"`, the `<button>` inside it is
 * naturally tabbable):
 *   - focus on a header cell that CONTAINS an interactive control → native Tab
 *     moves focus INTO that control;
 *   - focus already ON such a control → native Tab carries focus back out;
 *   - a PLAIN header cell with no interactive child → returns `false`, so AG
 *     Grid's default cell-to-cell Tab navigation is preserved.
 *
 * ESP-18510: the earlier implementation returned `true` for EVERY Tab, which
 * suppressed AG Grid's navigation even on plain header cells (e.g. the pinned
 * Param/Total/Average cells you land on first when tabbing into the CT grid).
 * That broke the deterministic cell-to-cell walk across the header row, so
 * keyboard users could not reliably reach drill-down buttons such as
 * "+MAR2021". This DOM-aware version mirrors the body-cell suppressor below so
 * the header row stays walkable and focus only steps into a cell when it
 * actually holds a control.
 *
 * Non-Tab keys are not suppressed, so arrow-key header navigation is unchanged.
 *
 * Use as the `suppressHeaderKeyboardEvent` callback in `defaultColDef`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function suppressHeaderTabToFocusChildren(params: any): boolean {
  if (params?.event?.key !== "Tab") return false;
  const active = (typeof document !== "undefined" ? document.activeElement : null) as HTMLElement | null;
  if (!active) return false;
  // Focus already on an interactive control → let native Tab move focus out
  // (in either direction) back onto the header row.
  if (active.matches?.(GRID_INTERACTIVE_CHILD_SELECTOR)) return true;
  // Focus on a header cell that merely HOLDS an interactive control.
  // Only forward Tab may step natively into that child, because the control
  // sits AFTER the cell in the DOM. For reverse Shift+Tab the child cannot be
  // reached that way, so defer to AG Grid's programmatic cell-to-cell
  // navigation; otherwise native Shift+Tab would skip past the roving
  // tabindex="-1" cells to the left (the reverse-navigation skip bug).
  if (params.event.shiftKey) return false;
  return !!active.querySelector?.(GRID_INTERACTIVE_CHILD_SELECTOR);
}

/**
 * Make an interactive control rendered inside a BODY cell reachable with the
 * Tab key (WCAG 2.2 SC 2.1.1 Keyboard / 4.1.2 Name, Role, Value).
 *
 * AG Grid's default body-cell keyboard handling treats Tab as cell-to-cell
 * navigation, so a focusable child of a cell (e.g. the MU Distribution
 * "validation message" error icon button, which only renders inside the
 * currently focused invalid cell) is skipped and never lands in the Tab order.
 *
 * Returning `true` from `suppressKeyboardEvent` tells AG Grid to stop its own
 * Tab handling WITHOUT calling preventDefault, so the browser's native Tab runs:
 *   - focus on a cell that CONTAINS an interactive control → native Tab moves
 *     focus INTO that control (the cell wrapper is tabindex="-1", the control is
 *     naturally tabbable);
 *   - focus already ON such a control → native Tab carries focus back out;
 *   - a plain cell with no interactive child → returns `false`, so AG Grid's
 *     default cell-to-cell Tab navigation is preserved.
 *
 * While a cell editor is open (`params.editing`) every key is left to AG Grid so
 * the editor still commits and advances to the next cell.
 *
 * ACTION SEPARATION (WCAG 2.1.1): the base / week distribution cells are BOTH
 * editable AND render the validation-error icon button. When focus is ON that
 * in-cell button, its native Enter / Space fire the button (open the popover);
 * AG Grid's `suppressKeyboardEvent` used to only intercept Tab, so those same
 * keys also bubbled to AG Grid and ALSO started the cell's edit action. This
 * suppressor now returns `true` for the control's activation keys whenever a
 * child control holds focus, so the icon performs ONLY the icon action. Focus
 * on the CELL itself (no child control focused) keeps AG Grid's default keys,
 * so the cell performs ONLY the edit action. Arrow keys are never suppressed,
 * so cell-to-cell navigation is unchanged.
 *
 * Use as the `suppressKeyboardEvent` callback in `defaultColDef` (compose with
 * `combineGridKeyboardSuppressors` if a grid also needs Home/End handling).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function suppressGridTabToFocusChildren(params: any): boolean {
  const key = params?.event?.key;
  if (!key) return false;
  // Let the cell editor own every key (commit + move to next cell).
  if (params.editing) return false;
  const active = (typeof document !== "undefined" ? document.activeElement : null) as HTMLElement | null;
  if (!active) return false;
  const onControl = !!active.matches?.(GRID_INTERACTIVE_CHILD_SELECTOR);
  if (key === "Tab") {
    // Focus already on an interactive control → let native Tab move focus out.
    if (onControl) return true;
    // Focus on a cell holding an interactive control → let native Tab move in.
    return !!active.querySelector?.(GRID_INTERACTIVE_CHILD_SELECTOR);
  }
  // Focus is ON an interactive control: let the control own its activation keys
  // (native Enter / Space open the popover) and stop AG Grid from ALSO acting on
  // them (starting the cell edit). Non-activation keys (arrows, etc.) keep AG
  // Grid's default behaviour.
  if (onControl && GRID_CONTROL_ACTIVATION_KEYS.has(key)) return true;
  return false;
}

/** Marker attribute used to find/reuse the shared status live region. */
const LIVE_REGION_MARKER = "data-esp-a11y-live-region";

/**
 * Announce a transient status message to screen readers through a shared,
 * visually-hidden `aria-live="polite"` region (WCAG 4.1.3 Status Messages).
 *
 * Some actions complete without moving focus or rendering any visible
 * confirmation — e.g. copying or pasting cells in a grid with Ctrl+C / Ctrl+V —
 * so a screen reader stays silent. Calling this after such an action gives
 * JAWS/NVDA a spoken confirmation (e.g. "Copied to clipboard", "Content pasted
 * successfully") without changing focus or the visible UI.
 *
 * One reusable region is created lazily under `host` and reused on later calls
 * (located via a data attribute). The text is cleared and then set on a short
 * timeout so that repeating the SAME message (e.g. two copies in a row) is
 * still a genuine DOM mutation and gets re-announced.
 *
 * Pass `assertive = true` for the immediate result of a user action that must
 * interrupt and be spoken now (e.g. a checkbox check/uncheck confirmation);
 * this uses a separate `aria-live="assertive"` / `role="alert"` region. Leave
 * it false (default) for incidental status so those stay polite.
 */
export function announceToScreenReader(
  host: HTMLElement | null | undefined,
  message: string,
  doc: Document = document,
  assertive = false
): void {
  if (!host || !message) return;

  // Polite and assertive announcements use SEPARATE regions: a region's
  // aria-live politeness is effectively fixed once assistive tech has taken it
  // into its accessibility tree, so one reused element cannot switch between
  // polite and assertive. Immediate results of a user action that must be
  // spoken right away (e.g. a checkbox check/uncheck confirmation) use the
  // assertive region; incidental status (navigation, result counts) stays
  // polite. JAWS frequently drops a polite status update fired inside a
  // combobox/listbox popup, so state changes there need the assertive channel.
  const marker = assertive ? `${LIVE_REGION_MARKER}-assertive` : LIVE_REGION_MARKER;
  let region = host.querySelector<HTMLElement>(`[${marker}]`);
  if (!region) {
    region = doc.createElement("span");
    region.setAttribute(marker, "");
    region.setAttribute("role", assertive ? "alert" : "status");
    region.setAttribute("aria-live", assertive ? "assertive" : "polite");
    region.setAttribute("aria-atomic", "true");
    // Visually hidden (clip technique) but kept in the accessibility tree.
    const s = region.style;
    s.position = "absolute";
    s.width = "1px";
    s.height = "1px";
    s.margin = "-1px";
    s.padding = "0";
    s.border = "0";
    s.overflow = "hidden";
    s.clip = "rect(0 0 0 0)";
    s.clipPath = "inset(50%)";
    s.whiteSpace = "nowrap";
    host.appendChild(region);
  }

  const liveRegion = region;
  // Clear first, then set on a timeout so an identical consecutive message is
  // a real DOM change and is re-announced by assistive technology.
  liveRegion.textContent = "";
  const view = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
  const setText = () => {
    liveRegion.textContent = message;
  };
  if (view) {
    view.setTimeout(setText, 50);
  } else {
    setText();
  }
}

/**
 * Build the accessible name for an AG Grid cell editor
 * (WCAG 4.1.2 Name, Role, Value).
 *
 * AG Grid renders numeric / time-or-decimal editors as a bare `<input>` with no
 * label, so Dragon NaturallySpeaking voice users could not recognise or target
 * them. Combining the row's parameter label with the column header (e.g.
 * "Contacts Received (Forecast), Mar 2021") makes the field's programmatic name
 * match the visible row + column headers so each edit field is uniquely
 * voice-addressable.
 *
 * Shared by NumericEditorComponent and TimeOrDecimalEditorComponent (identical
 * logic) via their `ariaLabel` getter. Pass the editor's `params` (the AG Grid
 * `ICellEditorParams`); returns `null` when neither a row label nor a column
 * header is available so the `[attr.aria-label]` binding omits the attribute.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildCellEditorAriaLabel(params: any): string | null {
  const row = params?.data?.plan_label_param ?? "";
  const col =
    params?.colDef?.headerName ??
    params?.column?.getColDef?.().headerName ??
    "";
  const name = [row, col].filter(Boolean).join(", ");
  return name || null;
}
